(function () {
  'use strict';

  var PRICING_PROXY_PATH = '/apps/aboffs-minion/pricing';
  var CART_FEES_PATH = '/apps/aboffs-minion/cart-fees';
  var DRAFT_CHECKOUT_PATH = '/apps/aboffs-minion/draft-checkout/v2';
  var LEGACY_STATE_OVERRIDE_STORAGE_KEY = 'mm_price_mask_state_override';
  var REGION_STATE_SESSION_STORAGE_KEY = 'aboffs_region_state';
  var RAAP_REGION_ID_QUERY_PARAM = 'region_id';
  var RAAP_REGION_ID_TO_STATE = {
    ny: 'NY',
    va: 'VA'
  };
  var SEARCH_SUGGESTIONS_RENDERED_EVENT = 'aboffs:searchSuggestionsRendered';
  var SEARCH_SUGGESTIONS_READY_EVENT = 'aboffs:searchSuggestionsReady';
  var PRODUCT_RECOMMENDATIONS_RENDERED_EVENT = 'aboffs:productRecommendationsRendered';
  var GEOLOCATION_API_URL =
    'https://geolocation.developers-efd.workers.dev/?x-api-key=5de3921b-dd40-4eb0-8578-236de4f2f3a4';

  var resolveStatePromise = null;
  var applyTimer = null;
  var applyInProgress = false;
  var applyQueued = false;
  var applyQueuedOptions = null;
  var pendingApplyOptions = null;
  var draftCheckoutIntent = null;
  var installSubmitPatchDone = false;
  var redirectedHiddenProductPage = false;
  var APPLY_MIN_INTERVAL_MS = 400;
  var lastApplyAt = 0;
  var DRAFT_INTENT_WINDOW_MS = 5000;
  var ZAPIET_CHECKOUT_METHOD_ATTR = 'Checkout-Method';
  var latestCartLineQty = Object.create(null);
  var listingGridLoads = typeof WeakMap !== 'undefined' ? new WeakMap() : null;
  var listingGridLoadsFallback = Object.create(null);
  var recommendationVariantIdByHandle = Object.create(null);
  var cartFeesInflight = null;
  var cartFeesCacheKey = '';
  var cartFeesCache = null;

  if (typeof Node !== 'undefined' && Node.prototype && !('isConnected' in Node.prototype)) {
    Object.defineProperty(Node.prototype, 'isConnected', {
      get: function () {
        return !this.ownerDocument || this.ownerDocument.contains(this);
      }
    });
  }

  if (typeof Element !== 'undefined' && Element.prototype && !Element.prototype.closest) {
    Element.prototype.closest = function (s) {
      var el = this;
      do {
        if (Element.prototype.matches && el.matches(s)) return el;
        if (Element.prototype.msMatchesSelector && el.msMatchesSelector(s)) return el;
        if (Element.prototype.webkitMatchesSelector && el.webkitMatchesSelector(s)) return el;
        el = el.parentElement || el.parentNode;
      } while (el !== null && el.nodeType === 1);
      return null;
    };
  }

  var US_STATE_NAME_TO_CODE = {
    alabama: 'AL',
    alaska: 'AK',
    arizona: 'AZ',
    arkansas: 'AR',
    california: 'CA',
    colorado: 'CO',
    connecticut: 'CT',
    delaware: 'DE',
    'district of columbia': 'DC',
    florida: 'FL',
    georgia: 'GA',
    hawaii: 'HI',
    idaho: 'ID',
    illinois: 'IL',
    indiana: 'IN',
    iowa: 'IA',
    kansas: 'KS',
    kentucky: 'KY',
    louisiana: 'LA',
    maine: 'ME',
    maryland: 'MD',
    massachusetts: 'MA',
    michigan: 'MI',
    minnesota: 'MN',
    mississippi: 'MS',
    missouri: 'MO',
    montana: 'MT',
    nebraska: 'NE',
    nevada: 'NV',
    'new hampshire': 'NH',
    'new jersey': 'NJ',
    'new mexico': 'NM',
    'new york': 'NY',
    'north carolina': 'NC',
    'north dakota': 'ND',
    ohio: 'OH',
    oklahoma: 'OK',
    oregon: 'OR',
    pennsylvania: 'PA',
    'rhode island': 'RI',
    'south carolina': 'SC',
    'south dakota': 'SD',
    tennessee: 'TN',
    texas: 'TX',
    utah: 'UT',
    vermont: 'VT',
    virginia: 'VA',
    washington: 'WA',
    'west virginia': 'WV',
    wisconsin: 'WI',
    wyoming: 'WY'
  };

  function appUrl(path) {
    try {
      return new URL(path, window.location.origin).toString();
    } catch (e) {
      return path;
    }
  }

  function normalizeStateCode(code) {
    var value = String(code || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(value) ? value : '';
  }

  function normalizeUSRegion(regionRaw) {
    var asCode = normalizeStateCode(regionRaw);
    if (asCode) return asCode;
    var key = String(regionRaw || '')
      .trim()
      .toLowerCase()
      .replace(/\./g, '')
      .replace(/-/g, ' ');
    return US_STATE_NAME_TO_CODE[key] || '';
  }

  function normalizeNonUSRegion(regionRaw) {
    var value = String(regionRaw || '')
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    return /^[A-Z0-9]{1,3}$/.test(value) ? value : '';
  }

  function normalizeRegionCode(countryCode, regionCodeRaw, regionRaw) {
    var cc = String(countryCode || '').trim().toUpperCase();
    if (cc === 'US') {
      return normalizeUSRegion(regionCodeRaw != null && regionCodeRaw !== '' ? regionCodeRaw : regionRaw);
    }
    var nonUs = normalizeNonUSRegion(regionCodeRaw);
    if (nonUs) return nonUs;
    return normalizeNonUSRegion(regionRaw);
  }

  function clearLegacyStateOverride() {
    try {
      if (window.localStorage) {
        window.localStorage.removeItem(LEGACY_STATE_OVERRIDE_STORAGE_KEY);
      }
    } catch (e) {}
  }

  function readCachedRegionState() {
    try {
      if (!window.sessionStorage) return '';
      return normalizeStateCode(window.sessionStorage.getItem(REGION_STATE_SESSION_STORAGE_KEY) || '');
    } catch (e) {
      return '';
    }
  }

  function writeCachedRegionState(state) {
    var code = normalizeStateCode(state || '');
    if (!code) return;
    try {
      if (window.sessionStorage) {
        window.sessionStorage.setItem(REGION_STATE_SESSION_STORAGE_KEY, code);
      }
    } catch (e) {}
  }

  function readQueryParam(name) {
    try {
      if (typeof URLSearchParams !== 'undefined' && window.location) {
        return String(new URLSearchParams(window.location.search).get(name) || '').trim();
      }
      var search = String((window.location && window.location.search) || '').replace(/^\?/, '');
      if (!search) return '';
      var parts = search.split('&');
      for (var i = 0; i < parts.length; i++) {
        var pair = parts[i].split('=');
        var key = decodeURIComponent((pair[0] || '').replace(/\+/g, ' '));
        if (key !== name) continue;
        return decodeURIComponent((pair[1] || '').replace(/\+/g, ' ')).trim();
      }
    } catch (e) {}
    return '';
  }

  function stateCodeFromRegionId(regionId) {
    var key = String(regionId || '').trim().toLowerCase();
    if (!key) return '';
    return normalizeStateCode(RAAP_REGION_ID_TO_STATE[key] || '');
  }

  function readStateFromRegionIdQuery() {
    return stateCodeFromRegionId(readQueryParam(RAAP_REGION_ID_QUERY_PARAM));
  }

  function removeListingProductItem(item) {
    if (!item || !item.parentNode) return;
    item.parentNode.removeChild(item);
  }

  function recommendationHandleFromItem(item) {
    if (!item || !item.querySelector) return '';
    var link = item.querySelector('a[href*="/products/"]');
    if (!link) return '';
    var href = String(link.getAttribute('href') || '');
    var match = href.match(/\/products\/([^/?#]+)/i);
    if (!match || !match[1]) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  function fetchRecommendationVariantIdByHandle(handle) {
    var key = String(handle || '').trim();
    if (!key) return Promise.resolve('');
    if (recommendationVariantIdByHandle[key]) return recommendationVariantIdByHandle[key];

    recommendationVariantIdByHandle[key] = fetch('/products/' + encodeURIComponent(key) + '.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then(function (productData) {
        var variants = productData && Array.isArray(productData.variants) ? productData.variants : [];
        if (!variants.length) return '';
        var selected = null;
        for (var i = 0; i < variants.length; i++) {
          if (variants[i] && variants[i].available) {
            selected = variants[i];
            break;
          }
        }
        if (!selected) selected = variants[0] || null;
        var id = selected && selected.id != null ? String(selected.id).trim() : '';
        return /^\d+$/.test(id) ? id : '';
      })
      .catch(function () {
        return '';
      });

    return recommendationVariantIdByHandle[key];
  }

  function hideRecommendationItem(item) {
    removeListingProductItem(item);
  }

  function unhideRecommendationItemIfLegacyHidden(item) {
    if (!item || !item.isConnected) return;
  }

  function applyLegacyRecommendationVisibility(state) {
    var stateCode = normalizeStateCode(state);
    if (!stateCode) return Promise.resolve();

    var items = document.querySelectorAll(
      '[data-product-recommendations-container] .thumbnail, .product-recommendations-container .thumbnail, .recommended-products-section .thumbnail'
    );
    if (!items.length) return Promise.resolve();

    var pending = [];
    for (var i = 0; i < items.length; i++) {
      (function (item) {
        if (!item || !item.isConnected) return;
        if (item.getAttribute('data-aboffs-legacy-state-check') === stateCode) return;
        if (item.querySelector('.js-price-override[data-variant-id]')) return;
        if (item.querySelector('.modal_price .current_price')) return;

        var handle = recommendationHandleFromItem(item);
        if (!handle) {
          item.setAttribute('data-aboffs-legacy-state-check', stateCode);
          return;
        }

        pending.push(
          fetchRecommendationVariantIdByHandle(handle)
            .then(function (variantId) {
              if (!variantId || !item.isConnected) return;
              return fetchOverridePrice(variantId, stateCode)
                .then(function (payload) {
                  if (!item.isConnected) return;
                  var visible = !payload || payload.isVisible !== false;
                  if (!visible) hideRecommendationItem(item);
                  else unhideRecommendationItemIfLegacyHidden(item);
                })
                .catch(function () {
                  if (!item.isConnected) return;
                  unhideRecommendationItemIfLegacyHidden(item);
                });
            })
            .finally(function () {
              if (!item.isConnected) return;
              item.setAttribute('data-aboffs-legacy-state-check', stateCode);
            })
        );
      })(items[i]);
    }

    if (!pending.length) return Promise.resolve();
    return Promise.all(pending).then(function () {
      return null;
    });
  }

  function fetchRegionCodeWithFallback() {
    return fetch(GEOLOCATION_API_URL, {
      credentials: 'omit',
      cache: 'no-store'
    })
      .then(function (res) {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then(function (data) {
        if (!data || data.message !== 'success' || !data.data) {
          throw new Error((data && data.message) || 'geolocation fail');
        }
        var geo = data.data;
        var countryCode = geo.country && geo.country.iso_code != null ? geo.country.iso_code : '';
        var regionCode = geo.region && geo.region.iso_code != null ? geo.region.iso_code : '';
        var regionName =
          geo.region && geo.region.names && geo.region.names.en != null ? geo.region.names.en : '';
        return normalizeRegionCode(countryCode, regionCode, regionName);
      });
  }

  function resolveStateCode() {
    if (resolveStatePromise) return resolveStatePromise;

    var fromRegionId = readStateFromRegionIdQuery();
    if (fromRegionId) {
      writeCachedRegionState(fromRegionId);
      resolveStatePromise = Promise.resolve(fromRegionId);
      return resolveStatePromise;
    }

    var cached = readCachedRegionState();
    if (cached) {
      resolveStatePromise = Promise.resolve(cached);
      return resolveStatePromise;
    }

    resolveStatePromise = fetchRegionCodeWithFallback().catch(function () {
      return '';
    });
    resolveStatePromise = resolveStatePromise.then(function (state) {
      var code = normalizeStateCode(state || '');
      if (!code) {
        resolveStatePromise = null;
        return '';
      }
      writeCachedRegionState(code);
      return code;
    });
    return resolveStatePromise;
  }

  function applyLocationBranding(stateCode) {
    var code = normalizeStateCode(stateCode || '');
    if (!code) return;

    var logos = document.querySelectorAll('img.primary_logo[data-aboffs-logo-default]');
    for (var i = 0; i < logos.length; i++) {
      var img = logos[i];
      var src = img.getAttribute('data-aboffs-logo-default') || '';
      if (code === 'NY') {
        var nySrc = img.getAttribute('data-aboffs-logo-ny');
        if (nySrc) src = nySrc;
      } else if (code === 'VA') {
        var vaSrc = img.getAttribute('data-aboffs-logo-va');
        if (vaSrc) src = vaSrc;
      }
      if (src && img.getAttribute('src') !== src) {
        img.setAttribute('src', src);
        if (img.dataset) img.dataset.src = src;
      }
    }

    if (code === 'NY' || code === 'VA') {
      var navs = document.querySelectorAll('[data-aboffs-location-nav]');
      for (var j = 0; j < navs.length; j++) {
        navs[j].classList.add('main-nav--center');
      }
    }
  }

  function getShopPermanentDomain() {
    var shop = '';
    if (typeof window.Shopify !== 'undefined' && window.Shopify) {
      if (typeof window.Shopify.shop === 'string') {
        shop = window.Shopify.shop;
      } else if (window.Shopify.shop && window.Shopify.shop.permanent_domain) {
        shop = String(window.Shopify.shop.permanent_domain);
      }
    }
    return shop;
  }

  function ensureDefaultDisplay(el) {
    if (!el) return;
    if (!el.getAttribute('data-default-display')) {
      var initialText = (el.textContent || '').trim();
      if (initialText) {
        el.setAttribute('data-default-display', initialText);
      }
    }
  }

  function fallbackDisplayFor(el) {
    if (!el) return '';
    var attr = (el.getAttribute('data-default-display') || '').trim();
    if (attr) return attr;
    return (el.textContent || '').trim();
  }

  function shouldShowPriceLoader(el) {
    if (!el || !el.closest) return false;
    if (el.classList) {
      if (
        el.classList.contains('js-cart-subtotal-money') ||
        el.classList.contains('js-cart-unit-price') ||
        el.classList.contains('js-cart-line-total')
      ) {
        return true;
      }
    }
    if (el.closest('.product-recommendations-container, .product-recommendations, .recommended-products--grid, .recommended-products--slider')) {
      return true;
    }
    if (el.closest('.product_section, .js-product_section, .product-quickshop, .js-quick-shop')) {
      return true;
    }
    if (el.closest('.search-matrix, .search-template-section, .template-search')) {
      return true;
    }
    if (el.closest('.collection-matrix, .collection-template-section, .template-collection')) {
      return true;
    }
    if (el.closest('.cart__blocks')) {
      return true;
    }
    return !!el.closest('[data-cart-item], #cart_form, .cart, .cart_content, .template-cart');
  }

  function hidePriceUntilHydrated(el) {
    if (!el) return;
    var root = document.documentElement;
    var shouldMask = false;
    if (root && root.classList && root.classList.contains('price-mask-init')) {
      shouldMask = true;
    }
    if (!shouldMask && el.classList && el.classList.contains('price-mask-pending')) {
      shouldMask = true;
    }
    if (!shouldMask) return;
    if (el.hasAttribute('data-price-hydrated')) return;
    if (!el.hasAttribute('data-price-pending')) {
      el.setAttribute('data-price-pending', '1');
      if (shouldShowPriceLoader(el)) {
        if (el.classList && el.classList.add) {
          el.classList.add('price-loader-active');
        }
        // Header mini-cart subtotal should never show stale totals while
        // state-based pricing is still resolving.
        if (
          el.classList &&
          el.classList.contains('js-cart-subtotal-money') &&
          el.closest('.mini_cart')
        ) {
          el.textContent = 'Loading...';
        }
        el.style.color = 'transparent';
        el.style.opacity = '1';
        el.style.visibility = 'visible';
      } else {
        el.style.opacity = '0';
        el.style.visibility = 'hidden';
      }
      // Hide immediately while pricing is pending so default theme prices
      // never flash during variant changes.
      el.style.transition = 'none';
    }
  }

  function revealListingTitle(priceEl) {
    if (!priceEl || !priceEl.closest) return;
    var details = priceEl.closest('.product-details');
    if (!details) return;
    var title = details.querySelector('.title');
    if (!title) return;
    title.style.visibility = 'visible';
    title.style.opacity = '1';
  }

  function revealHydratedPrice(el) {
    if (!el) return;
    el.setAttribute('data-price-hydrated', '1');
    el.removeAttribute('data-price-pending');
    if (el.classList && el.classList.remove) {
      el.classList.remove('price-mask-pending');
      el.classList.remove('price-loader-active');
    }
    el.style.color = '';
    el.style.visibility = 'visible';
    el.style.transition = 'opacity 180ms ease';
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () {
        if (!el.isConnected) return;
        el.style.opacity = '1';
        revealListingTitle(el);
      });
      return;
    }
    el.style.opacity = '1';
    revealListingTitle(el);
  }

  function parseMoneyTextToCents(text) {
    var normalized = String(text || '')
      .replace(/,/g, '')
      .replace(/[^0-9.\-]/g, '');
    var n = parseFloat(normalized);
    if (isNaN(n)) return 0;
    return Math.round(n * 100);
  }

  function formatMoneyCents(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(Math.round(cents), document.body.getAttribute('data-money-format') || '${{amount}}');
    }
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  function withShopQuery(url, shop) {
    if (!shop) return url;
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + 'shop=' + encodeURIComponent(shop);
  }

  function appendQueryParam(url, key, value) {
    if (value == null || value === '') return url;
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + encodeURIComponent(key) + '=' + encodeURIComponent(String(value));
  }

  function parsePriceRawToCents(raw) {
    if (raw == null || raw === '') return null;
    var n = parseFloat(
      String(raw)
        .replace(/,/g, '')
        .replace(/[^0-9.\-]/g, '')
    );
    if (isNaN(n)) return null;
    return Math.round(n * 100);
  }

  function normalizeFeeType(typeRaw) {
    var value = String(typeRaw || '').trim().toUpperCase();
    if (value === 'PERCENTAGE' || value === 'PERCENT' || value === 'PCT') return 'PERCENTAGE';
    if (value === 'FIXED') return 'FIXED';
    return '';
  }

  function normalizeSingleFeeDescriptor(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var feeType = normalizeFeeType(raw.type) || 'FIXED';
    var valueRaw = raw.value;
    var valueNumeric = parseFloat(String(valueRaw == null ? '' : valueRaw).replace(/,/g, ''));
    if (isNaN(valueNumeric) || valueNumeric < 0) valueNumeric = 0;
    var name = String(raw.name || '').trim();
    if (!name && !valueRaw && feeType !== 'PERCENTAGE') return null;
    return {
      name: name || 'Fee',
      type: feeType,
      valueRaw: valueRaw == null ? '' : String(valueRaw),
      valueNumeric: valueNumeric
    };
  }

  function feeDescriptorListFromPricingResponse(data) {
    if (!data || typeof data !== 'object') return [];

    if (Array.isArray(data.fees) && data.fees.length) {
      var fromArray = [];
      for (var i = 0; i < data.fees.length; i++) {
        var arrayDescriptor = normalizeSingleFeeDescriptor(data.fees[i]);
        if (arrayDescriptor) fromArray.push(arrayDescriptor);
      }
      if (fromArray.length) return fromArray;
    }

    if (data.fee && typeof data.fee === 'object') {
      var singleDescriptor = normalizeSingleFeeDescriptor(data.fee);
      if (singleDescriptor) return [singleDescriptor];
    }

    // Backward compatibility for old pricing payloads.
    if (data.paintCareFee != null && data.paintCareFee !== '') {
      var legacyCents = parsePriceRawToCents(data.paintCareFee);
      if (legacyCents != null && legacyCents > 0) {
        return [
          {
            name: 'PaintCare Fee',
            type: 'FIXED',
            valueRaw: String(data.paintCareFee),
            valueNumeric: legacyCents / 100
          }
        ];
      }
    }

    return [];
  }

  function feeDescriptorFromPricingResponse(data) {
    var list = feeDescriptorListFromPricingResponse(data);
    return list.length ? list[0] : null;
  }

  function feeCentsFromDescriptor(descriptor, baseUnitCents) {
    if (!descriptor) return 0;
    if (descriptor.type === 'PERCENTAGE') {
      if (baseUnitCents == null || baseUnitCents < 0) return 0;
      return Math.max(0, Math.round((baseUnitCents * descriptor.valueNumeric) / 100));
    }
    var cents = parsePriceRawToCents(descriptor.valueRaw);
    if (cents == null || cents < 0) return 0;
    return cents;
  }

  function catalogUnitCentsFromPricingResponse(data) {
    if (!data || data.ok === false || data.isVisible === false) return null;
    if (data.defaultPrice != null && data.defaultPrice !== '') {
      return parsePriceRawToCents(data.defaultPrice);
    }
    return null;
  }

  function baseUnitCentsFromPricingResponse(data) {
    if (!data) return null;
    if (data.ok === false) return null;
    if (data.isVisible === false) return null;
    // Pre-fee override base; overridePrice already includes assigned fees.
    if (data.baseOverridePrice != null && data.baseOverridePrice !== '') {
      return parsePriceRawToCents(data.baseOverridePrice);
    }
    if (data.effectivePrice != null && data.effectivePrice !== '') {
      return parsePriceRawToCents(data.effectivePrice);
    }
    var hasOverride = data.overridePrice != null && data.overridePrice !== '';
    if (hasOverride) return parsePriceRawToCents(data.overridePrice);
    if (data.defaultPrice != null && data.defaultPrice !== '') {
      return parsePriceRawToCents(data.defaultPrice);
    }
    return null;
  }

  function totalUnitCentsFromPricingResponse(data, baseUnitCents, feeCents) {
    if (feeCents <= 0) return baseUnitCents;
    if (data.baseOverridePrice != null && data.baseOverridePrice !== '') {
      return baseUnitCents + feeCents;
    }
    // When only overridePrice is returned, it already includes assigned fees.
    if (data.overridePrice != null && data.overridePrice !== '') {
      var overrideCents = parsePriceRawToCents(data.overridePrice);
      if (overrideCents != null && overrideCents === baseUnitCents) {
        return overrideCents;
      }
    }
    return baseUnitCents + feeCents;
  }

  function feeCentsFromPricingResponse(data, baseUnitCents) {
    var descriptors = feeDescriptorListFromPricingResponse(data);
    if (!descriptors.length) return 0;

    if (data.feeTotal != null && data.feeTotal !== '') {
      var totalFromApi = parsePriceRawToCents(data.feeTotal);
      if (totalFromApi != null && totalFromApi >= 0) return totalFromApi;
    }

    var total = 0;
    for (var i = 0; i < descriptors.length; i++) {
      total += feeCentsFromDescriptor(descriptors[i], baseUnitCents);
    }
    return total;
  }

  function fixedFeeCentsFromPricingResponse(data, baseUnitCents) {
    var descriptors = feeDescriptorListFromPricingResponse(data);
    if (!descriptors.length) return 0;
    var total = 0;
    for (var i = 0; i < descriptors.length; i++) {
      if (descriptors[i].type === 'PERCENTAGE') continue;
      total += feeCentsFromDescriptor(descriptors[i], baseUnitCents);
    }
    return total;
  }

  function percentageFeeCentsFromPricingResponse(data, baseUnitCents) {
    if (!data || typeof data !== 'object') return 0;
    if (data.percentageFeeTotal != null && data.percentageFeeTotal !== '') {
      var fromApi = parsePriceRawToCents(data.percentageFeeTotal);
      if (fromApi != null && fromApi >= 0) return fromApi;
    }
    var descriptors = feeDescriptorListFromPricingResponse(data);
    if (!descriptors.length) return 0;
    var total = 0;
    for (var i = 0; i < descriptors.length; i++) {
      if (descriptors[i].type !== 'PERCENTAGE') continue;
      total += feeCentsFromDescriptor(descriptors[i], baseUnitCents);
    }
    return total;
  }

  function checkoutUnitCentsFromPricingResponse(data, baseUnitCents) {
    if (baseUnitCents == null) return null;
    return baseUnitCents + fixedFeeCentsFromPricingResponse(data, baseUnitCents);
  }

  function feeNameFromPricingResponse(data) {
    var descriptors = feeDescriptorListFromPricingResponse(data);
    if (!descriptors.length) return '';
    if (descriptors.length === 1) return descriptors[0].name || 'Fee';
    return descriptors
      .map(function (descriptor) {
        return descriptor.name;
      })
      .filter(Boolean)
      .join(', ');
  }

  function feeLabelWithLocation(feeName, stateCode) {
    var feeLabel = String(feeName || 'Fee').trim();
    var locationLabel = locationLabelForFee(stateCode);
    if (!locationLabel) return feeLabel;
    return locationLabel + ' ' + feeLabel;
  }

  function feesPayloadFromPricingResponse(data, baseUnitCents, stateCode) {
    var descriptors = feeDescriptorListFromPricingResponse(data);
    var fees = [];
    for (var i = 0; i < descriptors.length; i++) {
      var descriptor = descriptors[i];
      var cents = feeCentsFromDescriptor(descriptor, baseUnitCents);
      if (cents <= 0 && descriptor.type !== 'PERCENTAGE') continue;
      if (descriptor.type === 'PERCENTAGE' && cents <= 0) {
        var pctFromApi = percentageFeeCentsFromPricingResponse(data, baseUnitCents);
        if (pctFromApi > 0) cents = pctFromApi;
      }
      if (cents <= 0) continue;
      fees.push({
        name: descriptor.name || 'Fee',
        type: descriptor.type || 'FIXED',
        percentage: descriptor.type === 'PERCENTAGE' ? String(descriptor.valueNumeric) : '',
        cents: cents,
        displayAmount: formatMoneyCents(cents),
        label: feeLabelWithLocation(descriptor.name, stateCode)
      });
    }
    return fees;
  }

  function fixedFeesFromPayload(fees) {
    if (!fees || !fees.length) return [];
    return fees.filter(function (fee) {
      return fee && fee.type !== 'PERCENTAGE';
    });
  }

  function percentageFeesFromPayload(fees) {
    if (!fees || !fees.length) return [];
    return fees.filter(function (fee) {
      return fee && fee.type === 'PERCENTAGE';
    });
  }

  function isVariantVisibleFromPricingResponse(data) {
    if (!data) return true;
    if (data.isVisible === false) return false;
    return true;
  }

  function fetchPricingJson(variantId, state) {
    var url = appUrl(PRICING_PROXY_PATH);
    var shop = getShopPermanentDomain();
    url = withShopQuery(url, shop);
    url = appendQueryParam(url, 'variant_id', String(variantId));
    url = appendQueryParam(url, 'state', state);
    return fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (text) {
          console.error('[aboffs-pricing] API error', {
            url: url,
            status: res.status,
            statusText: res.statusText,
            body: text
          });
          throw new Error(String(res.status));
        });
      }
      return res.json();
    });
  }

  function decimalStringFromCents(cents) {
    if (cents == null || isNaN(cents)) return '0.00';
    return (Math.round(cents) / 100).toFixed(2);
  }

  function discountCodesFromCart(cart) {
    var codes = [];
    if (!cart) return codes;
    var apps = cart.cart_level_discount_applications || [];
    for (var i = 0; i < apps.length; i++) {
      var title = apps[i] && apps[i].title;
      if (title && codes.indexOf(title) === -1) codes.push(title);
    }
    var items = cart.items || [];
    for (var j = 0; j < items.length; j++) {
      var discounts = items[j].discounts || [];
      for (var k = 0; k < discounts.length; k++) {
        var lineCode = discounts[k] && discounts[k].title;
        if (lineCode && codes.indexOf(lineCode) === -1) codes.push(lineCode);
      }
    }
    return codes;
  }

  function cartItemForRow(cart, row) {
    if (!cart || !cart.items || !row) return null;
    var lineId = parseInt(row.getAttribute('data-line-id'), 10);
    if (!isNaN(lineId) && lineId > 0) return cart.items[lineId - 1] || null;
    return null;
  }

  function cartLevelDiscountCents(cart) {
    var total = 0;
    if (!cart || !cart.cart_level_discount_applications) return total;
    for (var i = 0; i < cart.cart_level_discount_applications.length; i++) {
      var app = cart.cart_level_discount_applications[i];
      var cents = app && app.total_allocated_amount != null ? parseInt(app.total_allocated_amount, 10) : 0;
      if (!isNaN(cents) && cents > 0) total += cents;
    }
    return total;
  }

  function discountedUnitCentsForCartItem(overrideUnitCents, cartItem) {
    if (overrideUnitCents == null || !cartItem) return overrideUnitCents;
    var price = cartItem.price != null ? cartItem.price : null;
    var finalPrice = cartItem.final_price != null ? cartItem.final_price : null;
    if (price > 0 && finalPrice != null && finalPrice < price) {
      return Math.round((overrideUnitCents * finalPrice) / price);
    }
    return overrideUnitCents;
  }

  function discountedLineCentsForCartItem(overrideLineCents, cartItem) {
    if (overrideLineCents == null || !cartItem) return overrideLineCents;
    var qty = Math.max(1, cartItem.quantity || 1);
    // Keep line totals aligned with the displayed unit price * qty so
    // Shopify line-level rounding cannot introduce off-by-one cents.
    var unitOverrideCents = Math.round(overrideLineCents / qty);
    var discountedUnitCents = discountedUnitCentsForCartItem(unitOverrideCents, cartItem);
    if (discountedUnitCents != null) return discountedUnitCents * qty;
    return overrideLineCents;
  }

  function discountedUnitCentsForFeeRequest(overrideUnitCents, cartItem) {
    return discountedUnitCentsForCartItem(overrideUnitCents, cartItem);
  }

  function overrideDiscountCentsForLine(unitCents, qty, cartItem) {
    if (unitCents == null || !cartItem) return 0;
    var lineOriginalCents = unitCents * Math.max(1, qty || 1);
    var lineDiscountedCents = discountedLineCentsForCartItem(lineOriginalCents, cartItem);
    if (lineDiscountedCents == null || lineDiscountedCents >= lineOriginalCents) return 0;
    return lineOriginalCents - lineDiscountedCents;
  }

  function overrideDiscountCentsForAllocation(unitCents, qty, cartItem, shopifyAmountCents, shopifyLineDiscountTotalCents) {
    var lineOverrideDiscount = overrideDiscountCentsForLine(unitCents, qty, cartItem);
    if (lineOverrideDiscount <= 0) return shopifyAmountCents || 0;
    var shopifyTotal = shopifyLineDiscountTotalCents || 0;
    var shopifyAmount = shopifyAmountCents || 0;
    if (shopifyTotal > 0 && shopifyAmount > 0) {
      return Math.round(lineOverrideDiscount * (shopifyAmount / shopifyTotal));
    }
    return lineOverrideDiscount;
  }

  function discountTagIconHtml() {
    return (
      '<svg class="cart-discount-tag__icon-svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">' +
      '<path d="M7.5 1.5 12.5 6.5 6.5 12.5 1.5 7.5V1.5h6z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/>' +
      '<circle cx="4.25" cy="4.25" r="0.85" fill="currentColor"/>' +
      '</svg>'
    );
  }

  function removeDiscountCodeFromList(codes, codeToRemove) {
    var target = normalizeDiscountCode(codeToRemove).toLowerCase();
    if (!target) return (codes || []).slice();
    return (codes || []).filter(function (code) {
      return normalizeDiscountCode(code).toLowerCase() !== target;
    });
  }

  function renderCartDiscountTagHtml(code) {
    var safeCode = escapeHtmlText(code || 'Discount');
    var removeLabel = cartDiscountMessage('remove');
    return (
      '<span class="cart-discount-tag" data-aboffs-discount-tag>' +
      '<span class="cart-discount-tag__icon">' +
      discountTagIconHtml() +
      '</span>' +
      '<span class="cart-discount-tag__code">' +
      safeCode +
      '</span>' +
      '<button type="button" class="cart-discount-tag__remove" data-aboffs-discount-remove="' +
      safeCode +
      '" aria-label="' +
      escapeHtmlText(removeLabel + ' ' + (code || '')) +
      '">' +
      '<span aria-hidden="true">&times;</span>' +
      '</button>' +
      '</span>'
    );
  }

  function renderCartDiscounts(cart) {
    var containers = document.querySelectorAll('.js-cart_discounts, [data-aboffs-discount-tags]');
    var codes = discountCodesFromCart(cart);
    var html = '';
    for (var i = 0; i < codes.length; i++) {
      html += renderCartDiscountTagHtml(codes[i]);
    }
    for (var c = 0; c < containers.length; c++) {
      containers[c].innerHTML = html;
      if (html) {
        containers[c].hidden = false;
        containers[c].removeAttribute('hidden');
        containers[c].style.display = '';
      } else {
        containers[c].innerHTML = '';
        containers[c].hidden = true;
      }
      var parentTags = containers[c].closest('.mini-cart__discount-tags');
      if (parentTags) {
        if (html) {
          parentTags.hidden = false;
          parentTags.removeAttribute('hidden');
        } else {
          parentTags.hidden = true;
        }
      }
    }
    var forms = document.querySelectorAll('[data-cart-form]');
    for (var f = 0; f < forms.length; f++) {
      forms[f].setAttribute('data-total-discount', String((cart && cart.total_discount) || 0));
    }
  }

  function renderCartSavings(cart, savingsCentsOverride) {
    var nodes = document.querySelectorAll('[data-aboffs-cart-savings]');
    if (!nodes.length) return;
    var savingsCents =
      savingsCentsOverride != null && !isNaN(savingsCentsOverride)
        ? savingsCentsOverride
        : cart && cart.total_discount != null
          ? parseInt(cart.total_discount, 10)
          : 0;
    if (isNaN(savingsCents) || savingsCents <= 0) {
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].hidden = true;
      }
      return;
    }
    for (var j = 0; j < nodes.length; j++) {
      var node = nodes[j];
      var amountEl = node.querySelector('[data-aboffs-savings-amount]');
      if (amountEl) amountEl.textContent = formatMoneyCents(savingsCents);
      node.hidden = false;
      node.removeAttribute('hidden');
    }
  }

  function computeOverrideCartSavingsCents(pricingEntries, cart) {
    if (!pricingEntries || !pricingEntries.length || !cart) return null;
    var savingsCents = 0;
    var hasOverridePricing = false;
    for (var i = 0; i < pricingEntries.length; i++) {
      var entry = pricingEntries[i];
      if (!entry || !entry.payload) continue;
      hasOverridePricing = true;
      var cartItem = cartItemForRow(cart, entry.row);
      if (!cartItem) continue;
      var unitCents = cartDisplayUnitCentsFromPayload(entry.payload);
      if (unitCents == null) continue;
      savingsCents += overrideDiscountCentsForLine(unitCents, entry.quantity || 1, cartItem);
    }
    if (!hasOverridePricing) return null;
    var cartLevelDiscount = cartLevelDiscountCents(cart);
    if (cartLevelDiscount > 0) {
      savingsCents += cartLevelDiscount;
    }
    return savingsCents;
  }

  function renderCartLineDiscountNote(row, cartItem, unitCents, qty) {
    if (!row || !cartItem) return;
    var allocations = cartItem.line_level_discount_allocations || [];
    var existing = row.querySelectorAll('.cart__item--discount, .notification-discount');
    for (var i = 0; i < existing.length; i++) {
      existing[i].parentNode.removeChild(existing[i]);
    }
    if (!allocations.length) return;
    var anchor = row.querySelector('.modal_price, .price, .mini-cart__item--content .right.price');
    if (!anchor || !anchor.parentNode) return;
    var shopifyLineDiscountTotal = 0;
    for (var t = 0; t < allocations.length; t++) {
      var allocTotal = allocations[t] && allocations[t].amount != null ? allocations[t].amount : 0;
      shopifyLineDiscountTotal += allocTotal;
    }
    for (var j = 0; j < allocations.length; j++) {
      var allocation = allocations[j];
      if (!allocation) continue;
      var note = document.createElement('p');
      note.className = row.classList && row.classList.contains('mini-cart__item')
        ? 'notification-discount cart__item--discount-tag'
        : 'cart__item--discount cart__item--discount-tag';
      var title = allocation.discount_application && allocation.discount_application.title
        ? allocation.discount_application.title
        : 'Discount';
      var shopifyAmountCents = allocation.amount != null ? allocation.amount : 0;
      var amountCents =
        unitCents != null
          ? overrideDiscountCentsForAllocation(
              unitCents,
              qty,
              cartItem,
              shopifyAmountCents,
              shopifyLineDiscountTotal
            )
          : shopifyAmountCents;
      note.innerHTML =
        '<span class="cart-discount-tag__icon cart-discount-tag__icon--inline">' +
        discountTagIconHtml() +
        '</span>' +
        '<span class="cart__item--discount-text">' +
        escapeHtmlText(title) +
        ' (-<span class="money">' +
        formatMoneyCents(amountCents) +
        '</span>)</span>';
      anchor.parentNode.insertBefore(note, anchor.nextSibling);
    }
  }

  function normalizeDiscountCode(code) {
    return String(code || '').trim();
  }

  function mergeDiscountCodeList(existingCodes, code) {
    var codes = (existingCodes || []).slice();
    var normalized = normalizeDiscountCode(code);
    if (!normalized) return codes;
    var lower = normalized.toLowerCase();
    for (var i = 0; i < codes.length; i++) {
      if (String(codes[i] || '').trim().toLowerCase() === lower) return codes;
    }
    codes.push(normalized);
    return codes;
  }

  function isDiscountCodeApplied(cart, code) {
    var normalized = normalizeDiscountCode(code).toLowerCase();
    if (!normalized || !cart) return false;
    var apps = cart.cart_level_discount_applications || [];
    for (var i = 0; i < apps.length; i++) {
      if (String(apps[i].title || '').trim().toLowerCase() === normalized) return true;
    }
    var items = cart.items || [];
    for (var j = 0; j < items.length; j++) {
      var discounts = items[j].discounts || [];
      for (var k = 0; k < discounts.length; k++) {
        if (String(discounts[k].title || '').trim().toLowerCase() === normalized) return true;
      }
    }
    return false;
  }

  function cartDiscountMessage(key) {
    if (typeof Shopify !== 'undefined' && Shopify.translation) {
      if (key === 'applied' && Shopify.translation.cart_discount_applied) {
        return Shopify.translation.cart_discount_applied;
      }
      if (key === 'invalid' && Shopify.translation.cart_discount_invalid) {
        return Shopify.translation.cart_discount_invalid;
      }
      if (key === 'not_applied' && Shopify.translation.cart_discount_not_applied) {
        return Shopify.translation.cart_discount_not_applied;
      }
      if (key === 'remove' && Shopify.translation.cart_remove_discount) {
        return Shopify.translation.cart_remove_discount;
      }
    }
    if (key === 'applied') return 'Discount applied.';
    if (key === 'invalid') return 'Enter a valid discount code.';
    if (key === 'remove') return 'Remove discount';
    return 'That discount code could not be applied.';
  }

  function setCartDiscountMessage(message, type) {
    var nodes = document.querySelectorAll('[data-aboffs-discount-message]');
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (!message) {
        node.textContent = '';
        node.hidden = true;
        node.classList.remove('is-error', 'is-success');
        continue;
      }
      node.textContent = message;
      node.hidden = false;
      node.classList.remove('is-error', 'is-success');
      if (type) node.classList.add(type === 'error' ? 'is-error' : 'is-success');
    }
  }

  function fetchCartJson() {
    return fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  function applyCartDiscountCodes(codes) {
    var payload = { discount: (codes && codes.length ? codes.join(',') : '') || '' };
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (text) {
          throw new Error(text || String(res.status));
        });
      }
      return res.json();
    });
  }

  function buildCartFeesRequestLines(entries) {
    if (!entries || !entries.length) return [];
    return entries
      .map(function (entry) {
        var payload = entry.payload;
        if (!payload || entry.variantId == null) return null;
        var unitCents =
          payload.baseUnitCents != null
            ? payload.baseUnitCents
            : payload.unitCents != null
              ? payload.unitCents
              : entry.defaultUnitCents;
        if (unitCents == null) return null;
        var discountedCents =
          entry.discountedUnitCents != null && entry.discountedUnitCents !== unitCents
            ? entry.discountedUnitCents
            : null;
        var line = {
          variantId: String(entry.variantId),
          quantity: entry.quantity || 1,
          unitPrice: decimalStringFromCents(unitCents)
        };
        if (discountedCents != null) {
          line.discountedUnitPrice = decimalStringFromCents(discountedCents);
        }
        return line;
      })
      .filter(Boolean);
  }

  function fetchCartFees(lines, state, discountCodes) {
    var cacheKey =
      normalizeStateCode(state || '') +
      '|' +
      JSON.stringify(lines || []) +
      '|' +
      JSON.stringify(discountCodes || []);
    if (cartFeesCacheKey === cacheKey && cartFeesCache) {
      return Promise.resolve(cartFeesCache);
    }
    if (cartFeesInflight && cartFeesCacheKey === cacheKey) {
      return cartFeesInflight;
    }
    cartFeesCacheKey = cacheKey;
    var url = appUrl(CART_FEES_PATH);
    var shop = getShopPermanentDomain();
    url = withShopQuery(url, shop);
    cartFeesInflight = fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      body: JSON.stringify({
        state: normalizeStateCode(state || ''),
        lines: lines || [],
        discountCodes: discountCodes && discountCodes.length ? discountCodes : undefined
      })
    })
      .then(function (res) {
        if (!res.ok) {
          return res.text().then(function (text) {
            console.error('[aboffs-pricing] cart-fees API error', {
              url: url,
              status: res.status,
              body: text
            });
            throw new Error(String(res.status));
          });
        }
        return res.json();
      })
      .then(function (data) {
        cartFeesCache = data;
        return data;
      })
      .catch(function (err) {
        cartFeesCache = null;
        throw err;
      })
      .finally(function () {
        cartFeesInflight = null;
      });
    return cartFeesInflight;
  }

  function stripTrailingPercentageFromFeeName(name) {
    return String(name || '')
      .replace(/\s*\(\s*[\d.]+\s*%\s*\)\s*$/i, '')
      .trim();
  }

  function normalizePercentageDisplay(pctRaw) {
    var raw = String(pctRaw == null ? '' : pctRaw).trim();
    if (!raw) return '';
    var match = raw.match(/([\d.]+)\s*%?/);
    if (!match) return raw.replace(/%+$/g, '');
    return match[1];
  }

  function buildPercentageFeeLabel(feeName, pctRaw, stateCode) {
    var cleanName = stripTrailingPercentageFromFeeName(feeName) || 'Percentage fee';
    var locationLabel = locationLabelForFee(stateCode || '');
    var displayName = locationLabel ? locationLabel + ' ' + cleanName : cleanName;
    var pctText = normalizePercentageDisplay(pctRaw);
    if (pctText) return displayName + ' (' + pctText + '%)';
    return displayName;
  }

  function percentageFeesFromCartFeesResponse(data) {
    if (!data || data.ok === false) return null;
    if (data.reason === 'no_percentage_fees') return null;
    var feeTotalCents = parsePriceRawToCents(data.feeTotal);
    if (feeTotalCents == null || feeTotalCents <= 0) return null;
    var breakdown = [];
    var fees = Array.isArray(data.fees) ? data.fees : [];
    for (var i = 0; i < fees.length; i++) {
      var fee = fees[i];
      if (!fee) continue;
      var amountCents = parsePriceRawToCents(fee.amount);
      if (amountCents == null || amountCents <= 0) continue;
      var originalName = String(fee.name || 'Percentage fee').trim();
      var feeName = stripTrailingPercentageFromFeeName(originalName) || 'Percentage fee';
      var pctRaw = fee.percentage != null && fee.percentage !== '' ? String(fee.percentage).trim() : '';
      if (!pctRaw) {
        var namePct = originalName.match(/\(([\d.]+)\s*%\)/);
        if (namePct) pctRaw = namePct[1];
      }
      breakdown.push({
        name: feeName,
        percentage: pctRaw,
        label: buildPercentageFeeLabel(feeName, pctRaw, ''),
        amount: formatMoneyCents(amountCents),
        cents: amountCents
      });
    }
    return {
      title: 'Percentage fees',
      totalCents: feeTotalCents,
      totalDisplay: formatMoneyCents(feeTotalCents),
      breakdown: breakdown
    };
  }

  function overridePercentageFeeLineBaseCents(entries, cart) {
    if (!entries || !entries.length) return 0;
    var totalCents = 0;
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry || !entry.payload) continue;
      var unitCents = cartDisplayUnitCentsFromPayload(entry.payload);
      if (unitCents == null) continue;
      var cartItem = cart ? cartItemForRow(cart, entry.row) : null;
      var feeBaseUnitCents = discountedUnitCentsForCartItem(unitCents, cartItem);
      if (feeBaseUnitCents == null) feeBaseUnitCents = unitCents;
      var qty = Math.max(1, parseInt(entry.quantity, 10) || 1);
      totalCents += feeBaseUnitCents * qty;
    }
    return totalCents;
  }

  function shopifyPercentageFeeLineBaseCents(entries, cart) {
    if (!entries || !entries.length || !cart) return 0;
    var totalCents = 0;
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (!entry || !entry.payload) continue;
      var cartItem = cartItemForRow(cart, entry.row);
      if (!cartItem) continue;
      var qty = Math.max(1, parseInt(entry.quantity, 10) || 1);
      if (cartItem.final_line_price != null && cartItem.final_line_price > 0) {
        totalCents += cartItem.final_line_price;
        continue;
      }
      var unitCents = cartItem.final_price != null ? cartItem.final_price : cartItem.price;
      if (unitCents != null && unitCents > 0) totalCents += unitCents * qty;
    }
    return totalCents;
  }

  function entriesHaveOverridePricing(entries) {
    if (!entries || !entries.length) return false;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i] && entries[i].payload) return true;
    }
    return false;
  }

  function overridePercentageFeesFromApi(apiPercentageFees, entries, cart) {
    if (!apiPercentageFees || !entriesHaveOverridePricing(entries)) return apiPercentageFees;

    var breakdown = apiPercentageFees.breakdown || [];
    if (!breakdown.length) return apiPercentageFees;

    var overrideLineBaseCents = overridePercentageFeeLineBaseCents(entries, cart);
    if (overrideLineBaseCents <= 0) return apiPercentageFees;

    var newBreakdown = [];
    var newTotalCents = 0;
    for (var i = 0; i < breakdown.length; i++) {
      var row = breakdown[i];
      var pct = parseFloat(String(row.percentage == null ? '' : row.percentage).replace(/,/g, ''));
      var cents = 0;
      if (!isNaN(pct) && pct > 0) {
        cents = Math.max(0, Math.round((overrideLineBaseCents * pct) / 100));
      } else if (row.cents != null && row.cents > 0) {
        var shopifyLineBaseCents = shopifyPercentageFeeLineBaseCents(entries, cart);
        if (shopifyLineBaseCents > 0) {
          cents = Math.round(row.cents * (overrideLineBaseCents / shopifyLineBaseCents));
        } else {
          cents = row.cents;
        }
      }
      if (cents <= 0) continue;
      newBreakdown.push({
        name: stripTrailingPercentageFromFeeName(row.name) || row.name,
        percentage: row.percentage,
        label: buildPercentageFeeLabel(row.name, row.percentage, ''),
        amount: formatMoneyCents(cents),
        cents: cents
      });
      newTotalCents += cents;
    }

    if (!newBreakdown.length) return apiPercentageFees;
    return {
      title: apiPercentageFees.title || 'Percentage fees',
      totalCents: newTotalCents,
      totalDisplay: formatMoneyCents(newTotalCents),
      breakdown: newBreakdown
    };
  }

  function resolveCartPercentageFees(entries, cart, apiPercentageFees, stateCode) {
    if (apiPercentageFees && entriesHaveOverridePricing(entries)) {
      return overridePercentageFeesFromApi(apiPercentageFees, entries, cart);
    }
    if (apiPercentageFees) return apiPercentageFees;
    return buildConsolidatedPercentageCartFees(entries, cart, stateCode);
  }

  function mergeCartFeeDisplays(fixedFees, percentageFees) {
    var breakdown = [];
    var totalCents = 0;
    if (fixedFees && fixedFees.breakdown && fixedFees.breakdown.length) {
      breakdown = breakdown.concat(fixedFees.breakdown);
      totalCents += fixedFees.totalCents || 0;
    }
    if (percentageFees && percentageFees.breakdown && percentageFees.breakdown.length) {
      breakdown = breakdown.concat(percentageFees.breakdown);
      totalCents += percentageFees.totalCents || 0;
    } else if (percentageFees && percentageFees.totalCents) {
      totalCents += percentageFees.totalCents;
      breakdown.push({
        label: percentageFees.title || 'Percentage fees',
        amount: percentageFees.totalDisplay
      });
    }
    if (totalCents <= 0) return null;
    return {
      title: 'Additional Fees',
      totalCents: totalCents,
      totalDisplay: formatMoneyCents(totalCents),
      fixedFees: fixedFees,
      percentageFees: percentageFees,
      breakdown: breakdown
    };
  }

  function clearCartFeesCache() {
    cartFeesCacheKey = '';
    cartFeesCache = null;
    cartFeesInflight = null;
  }

  function displayFromPricingResponse(data) {
    var baseUnitCents = baseUnitCentsFromPricingResponse(data);
    if (baseUnitCents == null) return null;
    var checkoutCents = checkoutUnitCentsFromPricingResponse(data, baseUnitCents);
    return formatMoneyCents(checkoutCents != null ? checkoutCents : baseUnitCents);
  }

  function unitCentsFromPricingResponse(data) {
    var baseUnitCents = baseUnitCentsFromPricingResponse(data);
    if (baseUnitCents == null) return null;
    var checkoutCents = checkoutUnitCentsFromPricingResponse(data, baseUnitCents);
    return checkoutCents != null ? checkoutCents : baseUnitCents;
  }

  var FEE_NOTE_FONT_SIZE = '12px';

  function applyFeeNoteFontSize(noteEl) {
    if (!noteEl) return;
    noteEl.style.fontSize = FEE_NOTE_FONT_SIZE;
  }

  function isMiniCartRow(row) {
    return !!(row && row.classList && row.classList.contains('mini-cart__item'));
  }

  function updateLegacyFeeLabel(scope, text) {
    if (!scope || !scope.querySelectorAll) return;
    var labels = scope.querySelectorAll('.paintcare-text');
    for (var i = 0; i < labels.length; i++) {
      labels[i].textContent = text || '';
      if (text) applyFeeNoteFontSize(labels[i]);
    }
  }

  function locationLabelForFee(stateCode) {
    var code = normalizeStateCode(stateCode || '');
    if (!code) return '';
    if (code === 'NY') return 'NYS';
    return code;
  }

  function includedFeeTextForState(payload, state) {
    if (!payload) return '';
    var fixedFees = payload.fixedFees && payload.fixedFees.length ? payload.fixedFees : fixedFeesFromPayload(payload.fees);
    if (!fixedFees.length && !(payload.fixedFeeCents > 0)) return '';

    var payloadState = payload.stateCode ? normalizeStateCode(payload.stateCode) : '';
    var stateCode = payloadState || normalizeStateCode(state || '');
    var lines = [];

    for (var f = 0; f < fixedFees.length; f++) {
      lines.push('+ ' + fixedFees[f].label + ' ' + fixedFees[f].displayAmount);
    }

    if (!lines.length && payload.fixedFeeCents > 0) {
      var fallbackLabel = feeLabelWithLocation(payload.feeName || 'Fee', stateCode);
      lines.push('+ ' + fallbackLabel + ' ' + formatMoneyCents(payload.fixedFeeCents));
    }

    return lines.join('\n');
  }

  function ensurePaintCareNoteNode(priceEl) {
    if (!priceEl || !priceEl.closest) return null;
    var scope = priceEl.closest('.product_section, .product-quickshop, .js-product_section, .js-quick-shop');
    if (!scope) return null;
    var noteEl = scope.querySelector('.js-paint-care-fee-note');
    if (noteEl) return noteEl;
    noteEl = document.createElement('div');
    noteEl.className = 'js-paint-care-fee-note js-minion-fee-breakdown paint-care-fee-note';
    noteEl.style.display = 'none';
    applyFeeNoteFontSize(noteEl);
    noteEl.setAttribute('aria-live', 'polite');
    var anchor =
      scope.querySelector('.price') ||
      scope.querySelector('.modal_price') ||
      scope.querySelector('.product-info__price') ||
      scope.querySelector('.product-details__price');
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(noteEl, anchor.nextSibling);
      return noteEl;
    }
    scope.appendChild(noteEl);
    return noteEl;
  }

  function updatePaintCareFeeNote(priceEl, payload, state) {
    var noteEl = ensurePaintCareNoteNode(priceEl);
    var scope = priceEl && priceEl.closest
      ? priceEl.closest('.product_section, .product-quickshop, .js-product_section, .js-quick-shop')
      : null;
    var clear = function () {
      var hasLegacyLabel = scope && scope.querySelector('.paintcare-text');
      if (hasLegacyLabel) {
        updateLegacyFeeLabel(scope, '');
      } else if (noteEl) {
        noteEl.textContent = '';
        noteEl.style.display = 'none';
      }
    };

    if (!payload || !payload.hasFixedFee) {
      clear();
      return;
    }
    var includeText = includedFeeTextForState(payload, state);
    if (!includeText) {
      clear();
      return;
    }
    var hasLegacyLabel = scope && scope.querySelector('.paintcare-text');
    if (hasLegacyLabel) {
      updateLegacyFeeLabel(scope, includeText);
    } else if (noteEl) {
      noteEl.textContent = includeText;
      noteEl.style.display = '';
      applyFeeNoteFontSize(noteEl);
    }
  }

  var overridePriceCache = Object.create(null);
  var overridePriceInflight = Object.create(null);

  function fetchOverridePrice(variantId, state) {
    var resolvedState = normalizeStateCode(state || '') || '';
    var cacheKey = String(variantId) + ':' + resolvedState;
    if (overridePriceCache[cacheKey]) {
      return Promise.resolve(overridePriceCache[cacheKey]);
    }
    if (overridePriceInflight[cacheKey]) {
      return overridePriceInflight[cacheKey];
    }
    var promise = fetchPricingJson(variantId, resolvedState)
      .then(function (data) {
        var baseUnitCents = baseUnitCentsFromPricingResponse(data);
        var payloadStateCode = normalizeStateCode((data && data.state) || resolvedState || '');
        var fees = feesPayloadFromPricingResponse(data, baseUnitCents, payloadStateCode);
        var fixedFees = fixedFeesFromPayload(fees);
        var percentageFees = percentageFeesFromPayload(fees);
        var fixedFeeCents = fixedFees.reduce(function (sum, fee) {
          return sum + (fee.cents || 0);
        }, 0);
        var percentageFeeCents = percentageFeeCentsFromPricingResponse(data, baseUnitCents);
        if (percentageFeeCents <= 0 && percentageFees.length) {
          percentageFeeCents = percentageFees.reduce(function (sum, fee) {
            return sum + (fee.cents || 0);
          }, 0);
        }
        var checkoutUnitCents =
          baseUnitCents != null ? baseUnitCents + fixedFeeCents : null;
        var totalWithFeesCents =
          checkoutUnitCents != null ? checkoutUnitCents + percentageFeeCents : null;
        var payload = {
          display: displayFromPricingResponse(data),
          catalogUnitCents: catalogUnitCentsFromPricingResponse(data),
          baseUnitCents: baseUnitCents,
          unitCents: checkoutUnitCents,
          checkoutUnitCents: checkoutUnitCents,
          fixedFeeCents: fixedFeeCents,
          percentageFeeCents: percentageFeeCents,
          feeCents: fixedFeeCents,
          feeTotalCents: fixedFeeCents + percentageFeeCents,
          fees: fees,
          fixedFees: fixedFees,
          percentageFees: percentageFees,
          feeName: fees.length === 1 ? fees[0].name : feeNameFromPricingResponse(data),
          stateCode: payloadStateCode,
          hasFixedFee: fixedFeeCents > 0,
          hasPercentageFee: percentageFeeCents > 0,
          hasAssignedFee: fixedFeeCents > 0 || percentageFeeCents > 0,
          totalWithFeesCents: totalWithFeesCents,
          // Backward compatibility for existing callsites.
          paintCareFeeCents: fixedFeeCents,
          isVisible: isVariantVisibleFromPricingResponse(data)
        };
        overridePriceCache[cacheKey] = payload;
        return payload;
      })
      .finally(function () {
        delete overridePriceInflight[cacheKey];
      });
    overridePriceInflight[cacheKey] = promise;
    return promise;
  }

  function clearOverridePriceCache() {
    overridePriceCache = Object.create(null);
    clearCartFeesCache();
  }

  function isListingPriceRangeEl(el) {
    if (!el || !el.getAttribute) return false;
    if (el.getAttribute('data-price-varies') !== 'true') return false;
    return listingRangeVariantIdsToFetch(el).length > 1;
  }

  function addUniqueVariantId(ids, seen, id) {
    var value = String(id || '').trim();
    if (!/^\d+$/.test(value) || seen[value]) return;
    seen[value] = true;
    ids.push(value);
  }

  function parseVariantIdsAttr(el) {
    if (!el || !el.getAttribute) return [];
    var raw = String(el.getAttribute('data-variant-ids') || '').split(',');
    var seen = Object.create(null);
    var ids = [];
    for (var i = 0; i < raw.length; i++) {
      addUniqueVariantId(ids, seen, raw[i]);
    }
    return ids;
  }

  // Collection cards only need the catalog cheapest and most expensive
  // variants. Override prices from those two IDs become the displayed range.
  function listingRangeVariantIdsToFetch(el) {
    if (!el || !el.getAttribute) return [];
    var seen = Object.create(null);
    var ids = [];
    addUniqueVariantId(ids, seen, el.getAttribute('data-min-variant-id'));
    addUniqueVariantId(ids, seen, el.getAttribute('data-max-variant-id'));
    if (ids.length) return ids;
    return parseVariantIdsAttr(el);
  }

  function formatPriceRangeDisplay(minCents, maxCents) {
    if (minCents == null || maxCents == null) return '';
    if (minCents === maxCents) return formatMoneyCents(minCents);
    return formatMoneyCents(minCents) + ' to ' + formatMoneyCents(maxCents);
  }

  function listingRangeCentsFromPayload(payload) {
    if (!payload || payload.isVisible === false) return null;
    if (payload.unitCents != null) return payload.unitCents;
    if (payload.baseUnitCents != null) return payload.baseUnitCents;
    return null;
  }

  function applyListingPriceRange(el, state) {
    var variantIds = listingRangeVariantIdsToFetch(el);
    if (!variantIds.length) {
      revealFallbackProductPrice(el, state, resolveListingDefaultVariantId(el));
      return Promise.resolve();
    }

    var grid = findListingGrid(el);
    if (grid) beginListingGridFetch(grid);

    var promises = variantIds.map(function (variantId) {
      return fetchOverridePrice(variantId, state).catch(function () {
        return null;
      });
    });

    return Promise.all(promises)
      .then(function (payloads) {
        if (!el.isConnected) return;

        var visibleCents = [];
        var sawSuccessfulPayload = false;
        for (var i = 0; i < payloads.length; i++) {
          var payload = payloads[i];
          if (!payload) continue;
          sawSuccessfulPayload = true;
          var cents = listingRangeCentsFromPayload(payload);
          if (cents != null) visibleCents.push(cents);
        }

        if (!visibleCents.length) {
          if (sawSuccessfulPayload) {
            setPricingVisibility(el, false);
            revealHydratedPrice(el);
            return;
          }
          revealFallbackProductPrice(el, state, resolveListingDefaultVariantId(el));
          return;
        }

        var minCents = visibleCents[0];
        var maxCents = visibleCents[0];
        for (var j = 1; j < visibleCents.length; j++) {
          if (visibleCents[j] < minCents) minCents = visibleCents[j];
          if (visibleCents[j] > maxCents) maxCents = visibleCents[j];
        }

        setPricingVisibility(el, true);
        el.textContent = formatPriceRangeDisplay(minCents, maxCents);
        revealHydratedPrice(el);
      })
      .catch(function () {
        if (!el.isConnected) return;
        revealFallbackProductPrice(el, state, resolveListingDefaultVariantId(el));
      })
      .finally(function () {
        if (grid) completeListingGridFetch(grid);
      });
  }

  function resolveListingDefaultVariantId(el) {
    if (!el) return '';
    var fromAttr = String(el.getAttribute('data-variant-id') || '').trim();
    return /^\d+$/.test(fromAttr) ? fromAttr : '';
  }

  function resolveSingleVariantIdForPriceEl(el, variantId) {
    if (isProductDetailPriceEl(el)) {
      return resolveVariantIdNearPrice(el) || variantId;
    }
    return variantId;
  }

  function cartDisplayUnitCentsFromPayload(payload) {
    if (!payload) return null;
    if (payload.baseUnitCents != null) return payload.baseUnitCents;
    if (payload.unitCents != null) return payload.unitCents;
    return null;
  }

  function draftLineHasSplittableFees(pricing) {
    if (!pricing) return false;
    if (pricing.hasAssignedFee || pricing.hasFixedFee || pricing.hasPercentageFee) return true;
    if ((pricing.feeCents || 0) > 0) return true;
    if ((pricing.fixedFeeCents || 0) > 0) return true;
    if ((pricing.percentageFeeCents || 0) > 0) return true;
    return false;
  }

  function splitDraftCheckoutProductUnitCents(pricing, cartItem) {
    if (!pricing) return null;
    var baseCents =
      pricing.baseUnitCents != null ? pricing.baseUnitCents : cartDisplayUnitCentsFromPayload(pricing);
    if (baseCents == null) return null;
    return discountedUnitCentsForCartItem(baseCents, cartItem);
  }

  function applySplitProductPricingToDraftLine(line, cartItem) {
    if (!line || !line.pricing || !draftLineHasSplittableFees(line.pricing)) return line;
    var productUnitCents = splitDraftCheckoutProductUnitCents(line.pricing, cartItem);
    if (productUnitCents == null) return line;
    line.pricing.unitCents = productUnitCents;
    line.pricing.checkoutUnitCents = productUnitCents;
    line.pricing.discountedUnitCents = productUnitCents;
    line.originalUnitPriceCents = productUnitCents;
    line.discountedUnitCents = productUnitCents;
    return line;
  }

  function cartUnitDisplayFromPayload(payload) {
    var cents = cartDisplayUnitCentsFromPayload(payload);
    if (cents != null) return formatMoneyCents(cents);
    return payload ? payload.display : null;
  }

  function productPageDisplayFromPayload(payload) {
    if (!payload || payload.baseUnitCents == null) return null;
    return formatMoneyCents(payload.baseUnitCents);
  }

  function cartProductTitleFromRow(row) {
    if (!row || !row.querySelector) return '';
    var fromData = row.getAttribute('data-product-title');
    if (fromData) return fromData.trim();
    var titleLink =
      row.querySelector('.cart__item--title a') ||
      row.querySelector('.mini-cart__item__title .item_title a');
    if (titleLink) return (titleLink.textContent || '').trim();
    var titleEl = row.querySelector('.cart__item--title, .mini-cart__item__title .item_title');
    return titleEl ? (titleEl.textContent || '').trim() : '';
  }

  function feeCartItemTitle(feeLabel, productTitle) {
    var fee = String(feeLabel || 'Fee').trim().toUpperCase();
    var product = String(productTitle || '').trim();
    if (product) return fee + ' (' + product + ')';
    return fee;
  }

  function escapeHtmlText(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function locationLabelForConsolidatedFee(stateCode) {
    return locationLabelForFee(stateCode);
  }

  function consolidatedFeeLabelFromPayloads(payloads, stateCode) {
    var names = [];
    for (var i = 0; i < payloads.length; i++) {
      var payload = payloads[i];
      if (!payload) continue;
      var fees =
        payload.fixedFees && payload.fixedFees.length
          ? payload.fixedFees
          : fixedFeesFromPayload(payload.fees);
      if (fees && fees.length) {
        for (var j = 0; j < fees.length; j++) {
          var feeName = fees[j] && fees[j].name;
          if (feeName && names.indexOf(feeName) === -1) names.push(feeName);
        }
      } else if (payload.feeName && names.indexOf(payload.feeName) === -1) {
        names.push(payload.feeName);
      }
    }
    var code = normalizeStateCode(stateCode || '');
    if (code === 'NY') {
      return 'NYS Paint Care Fee';
    }
    var location = locationLabelForConsolidatedFee(stateCode);
    if (names.length === 1) {
      return location ? location + ' ' + names[0] : names[0];
    }
    if (names.length > 1) {
      return location ? location + ' additional fees' : 'Additional fees';
    }
    return location ? location + ' additional fees' : 'Additional fees';
  }

  function isDiscountMetaElement(el) {
    if (!el || !el.classList) return false;
    return (
      el.classList.contains('notification-discount') ||
      el.classList.contains('cart__item--discount') ||
      el.classList.contains('cart__item--discount-tag')
    );
  }

  function cartVariantLabelFromRow(row) {
    var productTitle = cartProductTitleFromRow(row);
    var variantParts = [];
    if (row && row.querySelectorAll) {
      var metas = row.querySelectorAll('.meta');
      for (var m = 0; m < metas.length; m++) {
        var meta = metas[m];
        if (isDiscountMetaElement(meta)) continue;
        var valueSpans = meta.querySelectorAll('span:not(.label)');
        for (var i = 0; i < valueSpans.length; i++) {
          var part = (valueSpans[i].textContent || '').trim();
          if (part && variantParts.indexOf(part) === -1) variantParts.push(part);
        }
      }
    }
    if (!variantParts.length && row && row.querySelector) {
      var link = row.querySelector('.mini-cart__item__title .item_title a');
      if (link) {
        var text = (link.textContent || '').trim();
        // Ignore nested line-item property nodes when reading the title.
        var clone = link.cloneNode(true);
        var nested = clone.querySelectorAll('.line-item');
        for (var n = 0; n < nested.length; n++) {
          if (nested[n].parentNode) nested[n].parentNode.removeChild(nested[n]);
        }
        text = (clone.textContent || '').replace(/\s+/g, ' ').trim();
        if (productTitle && text.indexOf(productTitle) === 0) {
          var remainder = text.slice(productTitle.length).replace(/^[\s\-–—/]+/, '').trim();
          if (remainder && remainder.toLowerCase() !== 'default title') variantParts.push(remainder);
        }
      }
    }
    if (variantParts.length) return productTitle + ' - ' + variantParts.join(' / ');
    return productTitle;
  }

  function feeCentsPerUnitFromPayload(payload) {
    if (!payload || !payload.hasAssignedFee) return 0;
    if (payload.fixedFeeCents > 0) return payload.fixedFeeCents;
    if (payload.feeCents > 0) return payload.feeCents;
    if (!payload.fixedFees || !payload.fixedFees.length) return 0;
    var total = 0;
    for (var i = 0; i < payload.fixedFees.length; i++) {
      total += payload.fixedFees[i].cents || 0;
    }
    return total;
  }

  function percentageFeeRateFromDescriptor(fee) {
    if (!fee) return 0;
    var pct = parseFloat(String(fee.percentage == null ? '' : fee.percentage).replace(/,/g, ''));
    if (!isNaN(pct) && pct > 0) return pct;
    if (typeof fee.valueNumeric === 'number' && !isNaN(fee.valueNumeric) && fee.valueNumeric > 0) {
      return fee.valueNumeric;
    }
    return 0;
  }

  function percentageFeeCentsFromDescriptor(fee, lineBaseCents) {
    if (!fee || lineBaseCents == null || lineBaseCents <= 0) return 0;
    var pct = percentageFeeRateFromDescriptor(fee);
    if (pct > 0) {
      return Math.max(0, Math.round((lineBaseCents * pct) / 100));
    }
    return fee.cents || 0;
  }

  function percentageFeeLabelFromDescriptor(fee, stateCode) {
    if (!fee) return 'Percentage fee';
    var feeName = String(fee.name || 'Percentage fee').trim();
    var pct = percentageFeeRateFromDescriptor(fee);
    if (!(pct > 0)) {
      var namePct = feeName.match(/\(([\d.]+)\s*%\)/);
      if (namePct) pct = parseFloat(namePct[1]);
    }
    return buildPercentageFeeLabel(feeName, pct > 0 ? pct : '', stateCode);
  }

  function buildConsolidatedPercentageCartFees(entries, cart, stateCode) {
    if (!entries || !entries.length) return null;
    var byKey = Object.create(null);
    var totalCents = 0;

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var payload = entry.payload;
      if (!payload) continue;
      var percentageFees =
        payload.percentageFees && payload.percentageFees.length
          ? payload.percentageFees
          : percentageFeesFromPayload(payload.fees);
      if (!percentageFees.length) continue;

      var unitCents = cartDisplayUnitCentsFromPayload(payload);
      if (unitCents == null) continue;
      var cartItem = cart ? cartItemForRow(cart, entry.row) : null;
      var feeBaseUnitCents = discountedUnitCentsForCartItem(unitCents, cartItem);
      if (feeBaseUnitCents == null) feeBaseUnitCents = unitCents;
      var qty = Math.max(1, parseInt(entry.quantity, 10) || 1);
      var lineBaseCents = feeBaseUnitCents * qty;

      for (var f = 0; f < percentageFees.length; f++) {
        var fee = percentageFees[f];
        var feeCents = percentageFeeCentsFromDescriptor(fee, lineBaseCents);
        if (feeCents <= 0) continue;
        var label = percentageFeeLabelFromDescriptor(fee, stateCode);
        var key = label;
        if (byKey[key]) {
          byKey[key].lineTotalCents += feeCents;
        } else {
          byKey[key] = { label: label, lineTotalCents: feeCents };
        }
        totalCents += feeCents;
      }
    }

    if (totalCents <= 0) return null;
    var breakdown = [];
    for (var key in byKey) {
      if (Object.prototype.hasOwnProperty.call(byKey, key)) breakdown.push(byKey[key]);
    }
    return {
      title: 'Percentage fees',
      totalCents: totalCents,
      totalDisplay: formatMoneyCents(totalCents),
      breakdown: breakdown.map(function (row) {
        return {
          label: row.label,
          amount: formatMoneyCents(row.lineTotalCents),
          cents: row.lineTotalCents
        };
      })
    };
  }

  function buildConsolidatedCartFees(entries, stateCode) {
    if (!entries || !entries.length) return null;
    var payloads = [];
    for (var p = 0; p < entries.length; p++) {
      if (entries[p].payload) payloads.push(entries[p].payload);
    }
    var feeLabel = consolidatedFeeLabelFromPayloads(payloads, stateCode);
    var byVariant = Object.create(null);

    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var payload = entry.payload;
      var feeCentsPerUnit = feeCentsPerUnitFromPayload(payload);
      if (feeCentsPerUnit <= 0) continue;

      var qty = parseInt(entry.quantity, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      var lineTotalCents = feeCentsPerUnit * qty;
      var variantLabel = entry.variantLabel || cartVariantLabelFromRow(entry.row);
      var breakdownKey = variantLabel + ' ( ' + feeLabel + ' )';
      var variantKey = String(entry.variantId || variantLabel);
      if (byVariant[variantKey]) {
        byVariant[variantKey].lineTotalCents += lineTotalCents;
      } else {
        byVariant[variantKey] = { breakdownKey: breakdownKey, lineTotalCents: lineTotalCents };
      }
    }

    var breakdown = [];
    for (var key in byVariant) {
      if (Object.prototype.hasOwnProperty.call(byVariant, key)) breakdown.push(byVariant[key]);
    }
    var totalCents = breakdown.reduce(function (sum, row) {
      return sum + row.lineTotalCents;
    }, 0);
    if (totalCents <= 0) return null;

    return {
      title: 'Additional Fees',
      totalCents: totalCents,
      totalDisplay: formatMoneyCents(totalCents),
      breakdown: breakdown.map(function (row) {
        return {
          label: row.breakdownKey,
          amount: formatMoneyCents(row.lineTotalCents)
        };
      })
    };
  }

  function ensureMiniCartAdditionalFeesContainer() {
    var miniCartForm = document.querySelector('[data-cart-form="mini-cart"]');
    if (!miniCartForm || miniCartForm.querySelector('[data-aboffs-additional-fees]')) return;

    var subtotalLi = miniCartForm.querySelector('.cart_subtotal');
    if (!subtotalLi || !subtotalLi.parentNode) return;

    var row = document.createElement('li');
    row.className = 'aboffs-additional-fees mini-cart__additional-fees';
    row.setAttribute('data-aboffs-additional-fees', '');
    row.hidden = true;
    row.innerHTML =
      '<div class="mini-cart__additional-fees__header">' +
      '<span class="mini-cart__additional-fees__title">Additional Fees</span>' +
      '</div>' +
      '<ul class="cart-fee-line__breakdown" data-aboffs-fee-breakdown></ul>' +
      '<p class="mini-cart__additional-fees__total">' +
      '<span class="money" data-aboffs-fee-total></span>' +
      '</p>';
    subtotalLi.parentNode.insertBefore(row, subtotalLi);
  }

  function ensureAdditionalFeesContainers() {
    ensureMiniCartAdditionalFeesContainer();
  }

  function hideAdditionalFeesRoot(root) {
    if (!root) return;
    root.hidden = true;
    root.setAttribute('hidden', '');
    root.style.display = 'none';
    var breakdownEl = root.querySelector('[data-aboffs-fee-breakdown]');
    var totalEl = root.querySelector('[data-aboffs-fee-total]');
    if (breakdownEl) breakdownEl.innerHTML = '';
    if (totalEl) totalEl.textContent = '';
  }

  function showAdditionalFeesRoot(root) {
    if (!root) return;
    root.hidden = false;
    root.removeAttribute('hidden');
    root.style.display = root.classList.contains('mini-cart__additional-fees') ? 'block' : '';
  }

  function renderAdditionalFeesRoot(root, fees) {
    if (!root) return;
    var totalEl = root.querySelector('[data-aboffs-fee-total]');
    var breakdownEl = root.querySelector('[data-aboffs-fee-breakdown]');
    if (!fees || !fees.totalCents) {
      hideAdditionalFeesRoot(root);
      return;
    }
    showAdditionalFeesRoot(root);
    if (totalEl) totalEl.textContent = fees.totalDisplay;
    if (breakdownEl) {
      breakdownEl.innerHTML = fees.breakdown
        .map(function (row) {
          return (
            '<li class="cart-fee-line__detail">' +
            '<span class="cart-fee-line__detail-label">' +
            escapeHtmlText(row.label) +
            '</span>' +
            '<span class="cart-fee-line__detail-amount">' +
            escapeHtmlText(row.amount) +
            '</span>' +
            '</li>'
          );
        })
        .join('');
    }
  }

  function renderConsolidatedCartFees(fees) {
    ensureAdditionalFeesContainers();
    var roots = document.querySelectorAll('[data-aboffs-additional-fees]');
    for (var i = 0; i < roots.length; i++) {
      renderAdditionalFeesRoot(roots[i], fees);
    }
  }

  function clearAllCartFeeItems() {
    var feeRows = document.querySelectorAll('[data-aboffs-fee-item]');
    for (var i = 0; i < feeRows.length; i++) {
      if (feeRows[i].parentNode) feeRows[i].parentNode.removeChild(feeRows[i]);
    }
  }

  function buildCartFeeItemRow(lineId, feeIndex, title, unitDisplay, lineTotalDisplay) {
    var row = document.createElement('div');
    row.className = 'section clearfix cart__item cart__item--fee';
    row.setAttribute('data-aboffs-fee-item', '1');
    row.setAttribute('data-aboffs-fee-for-line', lineId);
    row.setAttribute('data-aboffs-fee-index', String(feeIndex));

    var imageCol = document.createElement('div');
    imageCol.className = 'five columns small-down--one-fifth alpha cart__item--image cart__item--image--fee';
    imageCol.setAttribute('aria-hidden', 'true');

    var contentCol = document.createElement('div');
    contentCol.className = 'eleven columns small-down--four-fifths omega cart_content_info cart__item--content';

    var titleWrap = document.createElement('h3');
    titleWrap.className = 'cart__item--title';
    var titleSpan = document.createElement('span');
    titleSpan.textContent = title;
    titleWrap.appendChild(titleSpan);

    var priceWrap = document.createElement('p');
    priceWrap.className = 'modal_price';
    var unitMoney = document.createElement('span');
    unitMoney.className = 'money';
    unitMoney.textContent = unitDisplay;
    priceWrap.appendChild(unitMoney);

    var totalWrap = document.createElement('p');
    totalWrap.className = 'price_total';
    var totalStrong = document.createElement('strong');
    totalStrong.textContent = 'Total:';
    var totalMoney = document.createElement('span');
    totalMoney.className = 'money';
    totalMoney.textContent = lineTotalDisplay;
    totalWrap.appendChild(totalStrong);
    totalWrap.appendChild(document.createTextNode(' '));
    totalWrap.appendChild(totalMoney);

    contentCol.appendChild(titleWrap);
    contentCol.appendChild(priceWrap);
    contentCol.appendChild(totalWrap);
    row.appendChild(imageCol);
    row.appendChild(contentCol);
    return row;
  }

  function miniCartPriceClassFromRow(row) {
    var priceEl = row.querySelector('.price');
    if (priceEl && priceEl.classList.contains('right')) return 'right price';
    return 'left price';
  }

  function buildMiniCartFeeItemRow(lineId, feeIndex, title, unitDisplay, lineTotalDisplay, quantity, parentRow) {
    var row = document.createElement('li');
    row.className = 'mini-cart__item mini-cart__item--fee clearfix';
    row.setAttribute('data-aboffs-fee-item', '1');
    row.setAttribute('data-aboffs-fee-for-line', lineId);
    row.setAttribute('data-aboffs-fee-index', String(feeIndex));

    var imageCol = document.createElement('div');
    imageCol.className = 'cart_image mini-cart__item-image mini-cart__item-image--fee';
    imageCol.setAttribute('aria-hidden', 'true');

    var contentCol = document.createElement('div');
    contentCol.className = 'mini-cart__item--content';

    var titleWrap = document.createElement('div');
    titleWrap.className = 'mini-cart__item__title';
    var itemTitle = document.createElement('div');
    itemTitle.className = 'item_title';
    var titleSpan = document.createElement('span');
    titleSpan.textContent = title;
    itemTitle.appendChild(titleSpan);
    titleWrap.appendChild(itemTitle);

    var priceWrap = document.createElement('strong');
    priceWrap.className = miniCartPriceClassFromRow(parentRow);
    var priceMoney = document.createElement('span');
    priceMoney.className = 'money';
    priceMoney.textContent = quantity > 1 ? lineTotalDisplay : unitDisplay;
    priceWrap.appendChild(priceMoney);

    contentCol.appendChild(titleWrap);
    contentCol.appendChild(priceWrap);
    row.appendChild(imageCol);
    row.appendChild(contentCol);
    return row;
  }

  function buildFeeItemRow(parentRow, lineId, feeIndex, title, unitDisplay, lineTotalDisplay, quantity) {
    if (isMiniCartRow(parentRow)) {
      return buildMiniCartFeeItemRow(lineId, feeIndex, title, unitDisplay, lineTotalDisplay, quantity, parentRow);
    }
    return buildCartFeeItemRow(lineId, feeIndex, title, unitDisplay, lineTotalDisplay);
  }

  function updateCartFeeItems(row, payload, state, qty) {
    if (!payload || !payload.hasAssignedFee) return;

    var quantity = parseInt(qty, 10);
    if (isNaN(quantity) || quantity < 1) quantity = 1;
    var lineId = String(row.getAttribute('data-line-id') || '').trim();
    var productTitle = cartProductTitleFromRow(row);
    var stateCode = payload.stateCode ? normalizeStateCode(payload.stateCode) : normalizeStateCode(state || '');
    var feeEntries = [];
    var fees = payload.fees && payload.fees.length ? payload.fees : null;

    if (fees) {
      for (var i = 0; i < fees.length; i++) {
        var fee = fees[i];
        if (!fee.cents) continue;
        feeEntries.push({
          label: fee.label || fee.name || 'Fee',
          unitDisplay: fee.displayAmount,
          lineTotalDisplay: formatMoneyCents(fee.cents * quantity)
        });
      }
    } else if (payload.feeCents > 0) {
      feeEntries.push({
        label: feeLabelWithLocation(payload.feeName || 'Fee', stateCode),
        unitDisplay: formatMoneyCents(payload.feeCents),
        lineTotalDisplay: formatMoneyCents(payload.feeCents * quantity)
      });
    }

    if (!feeEntries.length) return;

    var insertAfter = row;
    for (var j = 0; j < feeEntries.length; j++) {
      var entry = feeEntries[j];
      var feeRow = buildFeeItemRow(
        row,
        lineId,
        j,
        feeCartItemTitle(entry.label, productTitle),
        entry.unitDisplay,
        entry.lineTotalDisplay,
        quantity
      );
      if (insertAfter.nextSibling) {
        insertAfter.parentNode.insertBefore(feeRow, insertAfter.nextSibling);
      } else {
        insertAfter.parentNode.appendChild(feeRow);
      }
      insertAfter = feeRow;
    }
  }

  function setCartCompareAtVisible(row, visible) {
    // compare-at price not shown in cart
  }

  function findExistingCompareAtEl(priceEl) {
    if (!priceEl || !priceEl.parentNode) return null;
    var next = priceEl.nextElementSibling;
    if (next && next.classList && next.classList.contains('js-compare-at-price')) return next;
    return priceEl.parentNode.querySelector('.js-compare-at-price');
  }

  function hideCompareAtEl(compareEl) {
    if (!compareEl) return;
    compareEl.setAttribute('aria-hidden', 'true');
    compareEl.style.display = 'none';
    compareEl.textContent = '';
  }

  function isSaleTemplateSuffix(suffix) {
    return String(suffix || '').trim().toLowerCase() === 'sale';
  }

  function isSaleTemplatePriceEl(priceEl) {
    return !!(priceEl && priceEl.closest && priceEl.closest('.sale-price-row'));
  }

  function compareAtCentsFromPriceEl(priceEl) {
    var cents = parseInt((priceEl && priceEl.getAttribute('data-compare-at-cents')) || '0', 10);
    return isNaN(cents) || cents < 0 ? 0 : cents;
  }

  function catalogCentsFromPriceEl(priceEl) {
    var cents = parseInt((priceEl && priceEl.getAttribute('data-catalog-cents')) || '0', 10);
    if (!isNaN(cents) && cents > 0) return cents;
    return parseMoneyTextToCents(fallbackDisplayFor(priceEl));
  }

  // SALE template products keep Shopify's sale price as the selling price.
  // A higher regional override is the pre-sale regular price and must not replace it.
  function saleSellingCents(payload, compareAtCents, catalogCentsOverride) {
    if (!payload || payload.isVisible === false) return null;
    var catalog = payload.catalogUnitCents != null ? payload.catalogUnitCents : catalogCentsOverride;
    var regional = payload.baseUnitCents;
    var compareAt = parseInt(compareAtCents, 10);
    if (isNaN(compareAt) || compareAt <= 0 || catalog == null || !(catalog < compareAt)) return null;
    if (regional != null && regional < catalog) return regional;
    return catalog;
  }

  function copyPlainObject(source) {
    var next = {};
    if (!source) return next;
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) next[key] = source[key];
    }
    return next;
  }

  function withSaleTemplateSellingPrice(payload, compareAtCents, catalogCentsOverride) {
    if (!payload || payload.isVisible === false) return payload;
    var saleBase = saleSellingCents(payload, compareAtCents, catalogCentsOverride);
    if (saleBase == null || payload.baseUnitCents == null || saleBase === payload.baseUnitCents) return payload;

    var next = copyPlainObject(payload);
    var fixed = payload.fixedFeeCents || 0;
    var percentageFees = payload.percentageFees || [];
    var nextPercentageFees = [];
    var percentageFeeCents = 0;
    for (var i = 0; i < percentageFees.length; i++) {
      var fee = percentageFees[i];
      if (!fee) continue;
      var feeCopy = copyPlainObject(fee);
      var cents = percentageFeeCentsFromDescriptor(fee, saleBase);
      feeCopy.cents = cents;
      feeCopy.displayAmount = formatMoneyCents(cents);
      nextPercentageFees.push(feeCopy);
      percentageFeeCents += cents;
    }
    next.baseUnitCents = saleBase;
    next.unitCents = saleBase + fixed;
    next.checkoutUnitCents = saleBase + fixed;
    next.percentageFeeCents = percentageFeeCents;
    next.percentageFees = nextPercentageFees;
    next.feeTotalCents = fixed + percentageFeeCents;
    next.totalWithFeesCents = saleBase + fixed + percentageFeeCents;
    next.hasPercentageFee = percentageFeeCents > 0;
    next.display = formatMoneyCents(saleBase + fixed);
    return next;
  }

  var productSaleContextByHandle = Object.create(null);

  function fetchProductSaleContext(handle) {
    var key = String(handle || '').trim();
    if (!key) return Promise.resolve(null);
    if (productSaleContextByHandle[key]) return productSaleContextByHandle[key];

    productSaleContextByHandle[key] = fetch(
      '/products/' + encodeURIComponent(key) + '?view=aboffs-sale-context',
      {
        method: 'GET',
        credentials: 'same-origin'
      }
    )
      .then(function (res) {
        if (!res.ok) throw new Error(String(res.status));
        return res.json();
      })
      .then(function (data) {
        var product = data && data.product ? data.product : null;
        var variants = Object.create(null);
        if (product && Array.isArray(product.variants)) {
          for (var i = 0; i < product.variants.length; i++) {
            var variant = product.variants[i];
            if (!variant || variant.id == null) continue;
            var compareAt = parseInt(variant.compare_at_price, 10);
            variants[String(variant.id)] = {
              compareAt: isNaN(compareAt) || compareAt < 0 ? 0 : compareAt
            };
          }
          return {
            templateSale: isSaleTemplateSuffix(product.template_suffix),
            variants: variants
          };
        }

        var source = data && data.variants ? data.variants : {};
        for (var id in source) {
          if (!Object.prototype.hasOwnProperty.call(source, id)) continue;
          var compareAtCents = parseInt(source[id], 10);
          variants[String(id)] = {
            compareAt: isNaN(compareAtCents) || compareAtCents < 0 ? 0 : compareAtCents
          };
        }
        return {
          templateSale: !!(data && data.templateSale) || isSaleTemplateSuffix(data && data.template_suffix),
          variants: variants
        };
      })
      .catch(function () {
        delete productSaleContextByHandle[key];
        return null;
      });

    return productSaleContextByHandle[key];
  }

  function payloadForSaleTemplateProduct(payload, handle, variantId) {
    if (!payload || payload.isVisible === false) return Promise.resolve(payload);
    if (payload.catalogUnitCents == null || payload.baseUnitCents == null) return Promise.resolve(payload);
    if (!(payload.baseUnitCents > payload.catalogUnitCents)) return Promise.resolve(payload);

    return fetchProductSaleContext(handle).then(function (context) {
      if (!context || !context.templateSale) return payload;
      var variant = context.variants && context.variants[String(variantId)];
      var compareAt = variant && variant.compareAt ? variant.compareAt : 0;
      return withSaleTemplateSellingPrice(payload, compareAt);
    });
  }

  function templateAlreadyShowsListPrice(priceEl) {
    if (!priceEl || !priceEl.closest) return false;
    var priceRoot = priceEl.closest('.modal_price, .price__container') || priceEl.parentNode;
    if (!priceRoot || !priceRoot.querySelector) return false;
    return !!priceRoot.querySelector('.was_price, .sale-price-row');
  }

  function saleTemplateMoneyHtml(display) {
    return '<span class="money">' + display + '</span>';
  }

  function syncSaleTemplatePrices(priceEl, payload) {
    if (!priceEl || !priceEl.closest) return;
    var root = priceEl.closest('.modal_price, .price__container');
    if (!root) return;
    var saleRow = root.querySelector('.sale-price-row');
    if (!saleRow) return;

    var wasPrice = root.querySelector('.was_price');
    var secondary = root.querySelector('.sale-price-secondary');
    var display = fallbackDisplayFor(priceEl) || (priceEl.textContent || '').trim();
    if (payload) {
      display = productPageDisplayFromPayload(payload) || payload.display || display;
    }
    if (!display) return;

    var unitCents =
      payload && payload.baseUnitCents != null
        ? payload.baseUnitCents
        : payload && payload.unitCents != null
          ? payload.unitCents
          : parseMoneyTextToCents(display);
    var compareAtCents = parseInt((priceEl.getAttribute('data-compare-at-cents') || '0'), 10);
    var onSale = compareAtCents > 0 && unitCents != null && unitCents > 0 && unitCents < compareAtCents;
    var priceInSecondary = !!(secondary && secondary.contains(priceEl));

    if (priceEl.classList) {
      if (onSale) priceEl.classList.add('sale');
      else priceEl.classList.remove('sale');
    }
    if (saleRow && saleRow.classList) {
      if (onSale) saleRow.classList.add('sale');
      else saleRow.classList.remove('sale');
    }

    if (onSale) {
      if (payload) {
        var saleDisplay = productPageDisplayFromPayload(payload);
        if (saleDisplay) priceEl.textContent = saleDisplay;
      }
      if (wasPrice) {
        wasPrice.innerHTML = saleTemplateMoneyHtml(formatMoneyCents(compareAtCents));
        wasPrice.classList.add('was_price--on-sale');
        wasPrice.style.display = '';
      }
      if (secondary) secondary.style.display = '';
      return;
    }

    if (!wasPrice) {
      if (secondary) secondary.style.display = priceInSecondary ? '' : 'none';
      return;
    }

    wasPrice.classList.remove('was_price--on-sale');
    if (priceInSecondary) {
      wasPrice.innerHTML = saleTemplateMoneyHtml(display);
      wasPrice.style.display = '';
      secondary.style.display = 'none';
      return;
    }

    wasPrice.innerHTML = '';
    wasPrice.style.display = 'none';
    if (secondary) secondary.style.display = 'none';
  }

  function findOrCreateCompareAtEl(priceEl) {
    if (!priceEl || !priceEl.parentNode) return null;
    var found = findExistingCompareAtEl(priceEl);
    if (found) return found;
    var s = document.createElement('s');
    s.className = 'js-compare-at-price';
    s.style.cssText = 'display:none;margin-left:14px;font-size:0.78em;font-weight:normal;opacity:0.7;vertical-align:middle;text-decoration:line-through;';
    s.setAttribute('aria-hidden', 'true');
    priceEl.parentNode.insertBefore(s, priceEl.nextSibling);
    return s;
  }

  function updateCompareAtEl(priceEl, payload) {
    if (templateAlreadyShowsListPrice(priceEl)) {
      hideCompareAtEl(findExistingCompareAtEl(priceEl));
      return;
    }
    var compareAtCents = parseInt((priceEl && priceEl.getAttribute('data-compare-at-cents')) || '0', 10);
    var compareEl = findOrCreateCompareAtEl(priceEl);
    if (!compareEl) return;
    var unitCents = payload && payload.unitCents != null ? payload.unitCents : null;
    if (compareAtCents > 0 && unitCents != null && unitCents < compareAtCents) {
      compareEl.textContent = formatMoneyCents(compareAtCents);
      compareEl.removeAttribute('aria-hidden');
      compareEl.style.display = 'inline';
      compareEl.style.marginLeft = '14px';
      compareEl.style.fontSize = '0.78em';
      compareEl.style.opacity = '0.7';
      compareEl.style.verticalAlign = 'middle';
      compareEl.style.textDecoration = 'line-through';
      compareEl.style.fontWeight = 'normal';
    } else {
      compareEl.setAttribute('aria-hidden', 'true');
      compareEl.style.display = 'none';
    }
  }

  function applySearchCompareAtDisplay(compareEl, priceEl, pricePayload, comparePayload) {
    if (!compareEl) return;
    var defaultCompareCents = parseMoneyTextToCents(fallbackDisplayFor(compareEl));
    var displayedPriceCents = pricePayload && pricePayload.unitCents != null ? pricePayload.unitCents : parseMoneyTextToCents(fallbackDisplayFor(priceEl));
    var maxCents = defaultCompareCents;

    if (pricePayload && pricePayload.unitCents != null && pricePayload.unitCents > maxCents) {
      maxCents = pricePayload.unitCents;
    }
    if (comparePayload && comparePayload.unitCents != null && comparePayload.unitCents > maxCents) {
      maxCents = comparePayload.unitCents;
    }

    if (maxCents > displayedPriceCents) {
      compareEl.textContent = formatMoneyCents(maxCents);
      compareEl.style.display = '';
    } else {
      compareEl.style.display = 'none';
    }

    revealHydratedPrice(compareEl);
  }

  function updateSearchCompareAtNode(priceEl, payload, state, priceVariantId) {
    if (!priceEl || !priceEl.closest) return;
    var priceWrap = priceEl.closest('.price');
    if (!priceWrap) return;
    var compareEl = priceWrap.querySelector('.js-search-compare-at');
    if (!compareEl) return;

    ensureDefaultDisplay(compareEl);
    hidePriceUntilHydrated(compareEl);

    var compareVariantId = String(compareEl.getAttribute('data-variant-id') || '').trim();
    var fromPriceVariant = String(priceVariantId || '').trim();

    if (/^\d+$/.test(compareVariantId) && compareVariantId !== fromPriceVariant) {
      fetchOverridePrice(compareVariantId, state)
        .then(function (comparePayload) {
          if (!compareEl.isConnected || !priceEl.isConnected) return;
          applySearchCompareAtDisplay(compareEl, priceEl, payload, comparePayload);
        })
        .catch(function () {
          if (!compareEl.isConnected || !priceEl.isConnected) return;
          applySearchCompareAtDisplay(compareEl, priceEl, payload, null);
        });
      return;
    }

    applySearchCompareAtDisplay(compareEl, priceEl, payload, null);
  }

  function resolveVariantIdNearPrice(el) {
    if (!el) return '';
    var scope = el.closest('.product_section, .product-quickshop, .js-product_section');
    if (scope) {
      var idField = scope.querySelector('select[name="id"], input[name="id"]');
      var fromField = idField && idField.value ? String(idField.value).trim() : '';
      if (/^\d+$/.test(fromField)) return fromField;
    }
    var productRoot = findProductPricingScope(el) || el.closest('[data-rv-handle], .gallery-cell');
    if (productRoot && productRoot.querySelector) {
      var rootIdField = productRoot.querySelector('select[name="id"], input[name="id"]');
      var fromRootField = rootIdField && rootIdField.value ? String(rootIdField.value).trim() : '';
      if (/^\d+$/.test(fromRootField)) return fromRootField;
    }
    var fromAttr = String(el.getAttribute('data-variant-id') || '').trim();
    if (/^\d+$/.test(fromAttr)) return fromAttr;
    return '';
  }

  function findProductVisibilityRoot(priceEl) {
    if (!priceEl || !priceEl.closest) return null;
    return (
      priceEl.closest('li.item-result') ||
      priceEl.closest('.product_row') ||
      priceEl.closest('.prod-item') ||
      priceEl.closest('[itemprop="itemListElement"]') ||
      priceEl.closest('.thumbnail[class*="product-"]') ||
      priceEl.closest('.thumbnail') ||
      priceEl.closest('.gallery-cell') ||
      priceEl.closest('.rv-box-element')
    );
  }

  function findListingGrid(priceEl) {
    if (!priceEl || !priceEl.closest) return null;
    return priceEl.closest('.aboffs-pricing-grid');
  }

  function isProductDetailPriceEl(priceEl) {
    if (!priceEl || !priceEl.closest) return false;
    if (findProductVisibilityRoot(priceEl) || findListingGrid(priceEl)) return false;
    return !!priceEl.closest('.js-product_section, .product_section, .product-quickshop, .js-quick-shop');
  }

  function isMainProductPageScope(scope) {
    if (!scope || !scope.closest) return false;
    if (scope.closest('.product-quickshop, .js-quick-shop')) return false;
    return !!scope.closest('.template-product');
  }

  function redirectHiddenProductPageToHome() {
    if (redirectedHiddenProductPage) return;
    redirectedHiddenProductPage = true;
    window.location.replace('/');
  }

  function getListingGridLoadState(grid) {
    if (!grid) return null;
    if (listingGridLoads) return listingGridLoads.get(grid) || null;
    return listingGridLoadsFallback[grid._aboffsGridId] || null;
  }

  function setListingGridLoadState(grid, state) {
    if (!grid) return;
    if (listingGridLoads) {
      if (state) listingGridLoads.set(grid, state);
      else listingGridLoads.delete(grid);
      return;
    }
    if (!grid._aboffsGridId) grid._aboffsGridId = 'grid-' + Date.now() + '-' + Math.random();
    if (state) listingGridLoadsFallback[grid._aboffsGridId] = state;
    else delete listingGridLoadsFallback[grid._aboffsGridId];
  }

  function ensureGridLoaderNode(grid) {
    if (!grid || grid.querySelector('.aboffs-pricing-grid__loader')) return;
    var loader = document.createElement('span');
    loader.className = 'aboffs-pricing-grid__loader';
    loader.setAttribute('role', 'status');
    loader.setAttribute('aria-live', 'polite');
    loader.setAttribute('aria-label', 'Loading products');
    grid.insertBefore(loader, grid.firstChild);
  }

  function shouldShowListingGridLoader(grid) {
    if (grid && grid.classList && grid.classList.contains('collection-matrix')) return false;
    if (!isHomepageProductTabsGrid(grid)) return true;
    return isActiveHomepageTabPanel(grid);
  }

  function beginListingGridFetch(grid) {
    if (!grid || !grid.classList) return;
    var state = getListingGridLoadState(grid);
    if (!state) {
      state = { pending: 0 };
      setListingGridLoadState(grid, state);
      if (shouldShowListingGridLoader(grid)) {
        grid.classList.add('aboffs-pricing-grid--loading');
        ensureGridLoaderNode(grid);
      }
    }
    state.pending++;
  }

  function clearListingGridItemStyles(grid) {
    if (!grid || !grid.querySelectorAll) return;
    var items = grid.querySelectorAll('.prod-item, .thumbnail, [itemprop="itemListElement"]');
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.classList || item.classList.contains('aboffs-pricing--hidden')) continue;
      item.style.visibility = '';
      item.style.opacity = '';
      item.style.pointerEvents = '';
    }
  }

  function getListingProductItems(grid) {
    if (!grid || !grid.querySelectorAll) return [];
    return grid.querySelectorAll('.prod-item, .thumbnail, [itemprop="itemListElement"]');
  }

  function countVisibleListingItems(grid) {
    var items = getListingProductItems(grid);
    var visible = 0;
    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      if (!item || !item.classList) continue;
      if (item.classList.contains('aboffs-pricing--hidden')) continue;
      visible++;
    }
    return visible;
  }

  function ensureListingGridEmptyMessage(grid) {
    if (!grid) return null;
    var existing = grid.querySelector('.aboffs-pricing-grid__empty');
    if (existing) return existing;
    var el = document.createElement('p');
    el.className = isHomepageProductTabsGrid(grid)
      ? 'aboffs-pricing-grid__empty empty-prod'
      : 'aboffs-pricing-grid__empty quote';
    el.setAttribute('hidden', '');
    el.setAttribute('aria-live', 'polite');
    el.textContent = "We're stocking up - check back soon!";
    grid.appendChild(el);
    return el;
  }

  function setListingGridEmptyVisible(emptyEl, visible) {
    if (!emptyEl) return;
    if (visible) {
      emptyEl.classList.add('is-visible');
      emptyEl.removeAttribute('hidden');
      emptyEl.style.display = '';
    } else {
      emptyEl.classList.remove('is-visible');
      emptyEl.setAttribute('hidden', '');
      emptyEl.style.display = 'none';
    }
  }

  function isProductRecommendationsContext(el) {
    if (!el || !el.closest) return false;
    return !!(
      el.closest('.recommended-products-section') ||
      el.closest('[data-product-recommendations-container]') ||
      el.closest('.product-recommendations-container')
    );
  }

  function findProductRecommendationsRoot(el) {
    if (!el || !el.closest) return null;
    return (
      el.closest('.recommended-products-section') ||
      el.closest('[data-product-recommendations-container]') ||
      el.closest('.product-recommendations-container')
    );
  }

  function updateProductRecommendationsSectionVisibility(scope) {
    var root = findProductRecommendationsRoot(scope) || scope;
    if (!root) return;
    // While recommendation pricing is still loading, do not show empty state yet.
    if (root.querySelector && root.querySelector('.aboffs-pricing-grid--loading')) {
      root.style.display = '';
      root.removeAttribute('aria-hidden');
      root.classList.remove('aboffs-recommendations--empty');
      return;
    }
    var total = getListingProductItems(root).length;
    var visible = countVisibleListingItems(root);
    if (total === 0 || visible === 0) {
      root.style.display = 'none';
      root.setAttribute('aria-hidden', 'true');
      root.classList.add('aboffs-recommendations--empty');
      return;
    }
    root.style.display = '';
    root.removeAttribute('aria-hidden');
    root.classList.remove('aboffs-recommendations--empty');
  }

  function finalizeAllProductRecommendationsVisibility() {
    var roots = document.querySelectorAll(
      '.recommended-products-section, [data-product-recommendations-container], .product-recommendations-container'
    );
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (!root.querySelector('.thumbnail, .prod-item, .gallery-cell')) continue;
      updateProductRecommendationsSectionVisibility(root);
    }
  }

  function updateListingGridEmptyState(grid) {
    if (!grid) return;
    if (isProductRecommendationsContext(grid)) {
      updateProductRecommendationsSectionVisibility(grid);
      return;
    }
    var items = getListingProductItems(grid);
    var total = items.length;
    var visible = countVisibleListingItems(grid);
    var pricingEmpty = grid.querySelector('.aboffs-pricing-grid__empty') || ensureListingGridEmptyMessage(grid);
    var liquidEmpty = grid.querySelector('p.empty-prod:not(.aboffs-pricing-grid__empty)');

    if (liquidEmpty) setListingGridEmptyVisible(liquidEmpty, false);
    setListingGridEmptyVisible(pricingEmpty, total === 0 || visible === 0);
  }

  function revealListingGridTitles(grid) {
    if (!grid || !grid.querySelectorAll) return;
    var titles = grid.querySelectorAll('.product-info__caption .product-details .title');
    for (var i = 0; i < titles.length; i++) {
      titles[i].style.visibility = 'visible';
      titles[i].style.opacity = '1';
    }
  }

  function releaseListingGrid(grid) {
    if (!grid || !grid.classList) return;
    grid.classList.remove('aboffs-pricing-grid--loading');
    clearListingGridItemStyles(grid);
    revealListingGridTitles(grid);
    updateListingGridEmptyState(grid);
    setListingGridLoadState(grid, null);
  }

  function completeListingGridFetch(grid) {
    if (!grid) return;
    var state = getListingGridLoadState(grid);
    if (!state) return;
    state.pending--;
    if (state.pending > 0) return;
    releaseListingGrid(grid);
    if (isHomepageProductTabsGrid(grid)) {
      grid.setAttribute('data-aboffs-tab-priced', '1');
    }
  }

  function isHomepageProductTabsGrid(grid) {
    return !!(grid && grid.classList && grid.classList.contains('tab-content-item'));
  }

  function isActiveHomepageTabPanel(panel) {
    if (!panel || !panel.classList) return false;
    if (panel.classList.contains('active')) return true;
    if (!panel.parentNode || !panel.parentNode.querySelector) return false;
    return !panel.parentNode.querySelector('.tab-content-item.active');
  }

  function prepareListingGridsForFetch() {
    var grids = document.querySelectorAll('.aboffs-pricing-grid');
    for (var i = 0; i < grids.length; i++) {
      var grid = grids[i];
      if (!grid.querySelector('.js-price-override[data-variant-id]')) {
        releaseListingGrid(grid);
        continue;
      }
      setListingGridLoadState(grid, { pending: 0 });
      if (shouldShowListingGridLoader(grid)) {
        grid.classList.add('aboffs-pricing-grid--loading');
        ensureGridLoaderNode(grid);
      } else {
        grid.classList.remove('aboffs-pricing-grid--loading');
      }
    }
  }

  function finalizeListingGridsWithoutFetches() {
    var grids = document.querySelectorAll('.aboffs-pricing-grid.aboffs-pricing-grid--loading');
    for (var i = 0; i < grids.length; i++) {
      var grid = grids[i];
      var state = getListingGridLoadState(grid);
      if (!state || state.pending <= 0) {
        releaseListingGrid(grid);
      }
    }
  }

  function prepareHomepageTabsForPricingRefresh() {
    var panels = document.querySelectorAll('.product-tabs .tab-content-item.aboffs-pricing-grid');
    for (var i = 0; i < panels.length; i++) {
      panels[i].removeAttribute('data-aboffs-tab-priced');
    }
  }

  function syncHomepageTabPanelActive(panel) {
    if (!panel || !panel.classList) return;
    var tabsRoot = panel.closest('.product-tabs');
    if (!tabsRoot) return;
    var panels = tabsRoot.querySelectorAll('.tab-content-item');
    for (var i = 0; i < panels.length; i++) {
      panels[i].classList.remove('active');
    }
    panel.classList.add('active');
  }

  function fetchListingPanelIfNeeded(panel, options) {
    options = options || {};
    if (!panel) return Promise.resolve();
    if (!options.force && panel.getAttribute('data-aboffs-tab-priced') === '1') {
      return Promise.resolve();
    }
    var inflight = getListingGridLoadState(panel);
    if (inflight && inflight.pending > 0) {
      if (options.showLoader !== false && isActiveHomepageTabPanel(panel)) {
        panel.classList.add('aboffs-pricing-grid--loading');
        ensureGridLoaderNode(panel);
      }
      return Promise.resolve();
    }
    var nodes = panel.querySelectorAll('.js-price-override[data-variant-id]');
    if (!nodes.length) {
      releaseListingGrid(panel);
      updateListingGridEmptyState(panel);
      panel.setAttribute('data-aboffs-tab-priced', '1');
      return Promise.resolve();
    }
    panel.setAttribute('data-aboffs-tab-priced', '1');
    var showLoader = options.showLoader !== false && isActiveHomepageTabPanel(panel);
    return resolveStateCode().then(function (state) {
      var promises = [];
      for (var i = 0; i < nodes.length; i++) {
        (function (el) {
          var variantId = resolveVariantIdNearPrice(el);
          if (!variantId && !isListingPriceRangeEl(el)) return;
          if (variantId) el.setAttribute('data-variant-id', variantId);
          markNodePendingForReprice(el);
          promises.push(applyPriceNode(el, state, variantId));
        })(nodes[i]);
      }
      if (!promises.length) {
        releaseListingGrid(panel);
        finalizeAllListingEmptyStates();
        return null;
      }
      setListingGridLoadState(panel, { pending: 0 });
      if (showLoader) {
        panel.classList.add('aboffs-pricing-grid--loading');
        ensureGridLoaderNode(panel);
      } else {
        panel.classList.remove('aboffs-pricing-grid--loading');
      }
      return Promise.all(promises).finally(function () {
        finalizeListingGridsWithoutFetches();
        finalizeAllListingEmptyStates();
      });
    });
  }

  function setPricingVisibility(priceEl, visible) {
    if (!priceEl) return;
    var listingRoot = findProductVisibilityRoot(priceEl);
    if (listingRoot) {
      if (visible) {
        listingRoot.classList.remove('aboffs-pricing--hidden');
        listingRoot.removeAttribute('aria-hidden');
        listingRoot.style.display = '';
      } else {
        removeListingProductItem(listingRoot);
      }
      return;
    }
    var pdpScope = priceEl.closest('.js-product_section, .product_section, .product-quickshop, .js-quick-shop');
    if (!pdpScope) return;
    if (visible) {
      pdpScope.classList.remove('aboffs-pricing--hidden');
      pdpScope.removeAttribute('aria-hidden');
      pdpScope.style.display = '';
    } else {
      if (isMainProductPageScope(pdpScope)) {
        redirectHiddenProductPageToHome();
        return;
      }
      pdpScope.classList.add('aboffs-pricing--hidden');
      pdpScope.setAttribute('aria-hidden', 'true');
      pdpScope.style.display = 'none';
    }
  }

  function applyPriceAndVisibility(el, payload, state, variantId) {
    var visible = !payload || payload.isVisible !== false;
    setPricingVisibility(el, visible);
    if (!visible) {
      updateCompareAtEl(el, null);
      revealHydratedPrice(el);
      return;
    }
    var isProductDetail = isProductDetailPriceEl(el);
    if (payload && isProductDetail && isSaleTemplatePriceEl(el)) {
      payload = withSaleTemplateSellingPrice(
        payload,
        compareAtCentsFromPriceEl(el),
        catalogCentsFromPriceEl(el)
      );
    }
    if (payload && payload.catalogUnitCents != null) {
      el.setAttribute('data-catalog-cents', String(payload.catalogUnitCents));
    }
    var display = payload && payload.display ? payload.display : fallbackDisplayFor(el);
    if (payload && isProductDetail) {
      display = productPageDisplayFromPayload(payload) || display;
    }
    el.textContent = display;
    if (isProductDetail) updateCompareAtEl(el, payload);
    if (isProductDetail) updatePaintCareFeeNote(el, payload, state);
    if (isProductDetail) syncSaleTemplatePrices(el, payload);
    if (variantId) updateSearchCompareAtNode(el, payload, state, variantId);
    revealHydratedPrice(el);
  }

  function revealFallbackProductPrice(el, state, variantId) {
    if (!el) return;
    setPricingVisibility(el, true);
    var fallback = fallbackDisplayFor(el);
    if (fallback) el.textContent = fallback;
    if (isProductDetailPriceEl(el)) {
      updateCompareAtEl(el, null);
      updatePaintCareFeeNote(el, null, state);
      syncSaleTemplatePrices(el, null);
    }
    if (variantId) updateSearchCompareAtNode(el, null, state, variantId);
    revealHydratedPrice(el);
  }

  function applyPriceNode(el, state, variantId) {
    if (isListingPriceRangeEl(el)) {
      return applyListingPriceRange(el, state);
    }

    variantId = resolveSingleVariantIdForPriceEl(el, variantId);
    if (!variantId) {
      revealFallbackProductPrice(el, state, variantId);
      return Promise.resolve();
    }

    var grid = findListingGrid(el);
    if (grid) beginListingGridFetch(grid);
    return fetchOverridePrice(variantId, state)
      .then(function (payload) {
        if (!el.isConnected) return;
        applyPriceAndVisibility(el, payload, state, variantId);
      })
      .catch(function () {
        if (!el.isConnected) return;
        revealFallbackProductPrice(el, state, variantId);
      })
      .finally(function () {
        if (grid) completeListingGridFetch(grid);
      });
  }

  function normalizeApplyOptions(options) {
    options = options || {};
    return {
      scope: options.scope === 'product' || options.scope === 'cart' ? options.scope : 'full',
      root: options.root || null
    };
  }

  function mergeApplyOptions(existing, incoming) {
    existing = normalizeApplyOptions(existing);
    incoming = normalizeApplyOptions(incoming);
    if (existing.scope === 'full' || incoming.scope === 'full') {
      return { scope: 'full', root: null };
    }
    if (existing.scope === 'cart' || incoming.scope === 'cart') {
      return { scope: 'cart', root: null };
    }
    return {
      scope: 'product',
      root: incoming.root || existing.root
    };
  }

  function findProductPricingScope(node) {
    if (!node || !node.closest) return null;

    var outer =
      node.closest('[data-rv-handle]') ||
      node.closest('[class*="product-"]') ||
      node.closest('.product__container') ||
      node.closest('.gallery-cell');
    if (outer && outer !== document.body && outer !== document.documentElement) {
      var hasPrice = outer.querySelector('.modal_price .current_price, .js-price-override[data-variant-id]');
      var hasId = outer.querySelector('select[name="id"], input[name="id"]');
      if (hasPrice && hasId) return outer;
    }

    var section = node.closest('.product_section, .js-product_section, .product-quickshop, .js-quick-shop');
    if (!section) return null;

    var pricedAncestor = section.parentElement;
    if (
      pricedAncestor &&
      pricedAncestor.querySelector('.modal_price .current_price, .js-price-override[data-variant-id]') &&
      !section.contains(
        pricedAncestor.querySelector('.modal_price .current_price, .js-price-override[data-variant-id]')
      )
    ) {
      return pricedAncestor;
    }
    return section;
  }

  function scheduleProductVariantPricing(target) {
    var productScope = findProductPricingScope(target);
    if (!productScope) return;
    var applyProductPricing = function () {
      scheduleApplyImmediate({
        scope: 'product',
        root: productScope
      });
    };
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(applyProductPricing);
      return;
    }
    setTimeout(applyProductPricing, 0);
  }

  function applyDirectPriceNodes(state, options) {
    options = normalizeApplyOptions(options);
    var applyScope = options.scope;
    var root = options.root;
    if (applyScope === 'product' && !root) return Promise.resolve();
    var searchRoot = applyScope === 'product' ? root : document;

    if (applyScope === 'full') {
      prepareHomepageTabsForPricingRefresh();
      prepareListingGridsForFetch();
    }

    var promises = [];
    var nodes = searchRoot.querySelectorAll('.js-price-override[data-variant-id]');
    for (var i = 0; i < nodes.length; i++) {
      (function (el) {
        var variantId = resolveVariantIdNearPrice(el);
        if (variantId) el.setAttribute('data-variant-id', variantId);
        markNodePendingForReprice(el);
        promises.push(applyPriceNode(el, state, variantId));
      })(nodes[i]);
    }

    // Turbo templates render many PDP prices as `.modal_price .current_price`.
    var fallbackNodes = searchRoot.querySelectorAll('.modal_price .current_price:not(.js-price-override)');
    for (var j = 0; j < fallbackNodes.length; j++) {
      (function (el) {
        var variantId = resolveVariantIdNearPrice(el);
        markNodePendingForReprice(el);
        promises.push(applyPriceNode(el, state, variantId));
      })(fallbackNodes[j]);
    }

    if (!promises.length) {
      if (applyScope !== 'product') {
        return applyLegacyRecommendationVisibility(state).finally(function () {
          finalizeListingGridsWithoutFetches();
          finalizeAllListingEmptyStates();
        });
      }
      return Promise.resolve();
    }

    return Promise.all(promises).finally(function () {
      if (applyScope === 'product') return;
      return applyLegacyRecommendationVisibility(state).finally(function () {
        finalizeListingGridsWithoutFetches();
        finalizeAllListingEmptyStates();
      });
    });
  }

  function finalizeAllListingEmptyStates() {
    var grids = document.querySelectorAll(
      '.aboffs-pricing-grid, .product-list.collection-matrix, .product-list.search-matrix, .product-tabs .tab-content-item'
    );
    for (var i = 0; i < grids.length; i++) {
      updateListingGridEmptyState(grids[i]);
    }
    finalizeAllProductRecommendationsVisibility();
  }

  function cacheCartLineQuantities(cart) {
    latestCartLineQty = Object.create(null);
    if (!cart || !cart.items || !cart.items.length) return;
    for (var i = 0; i < cart.items.length; i++) {
      var q = parseInt(cart.items[i] && cart.items[i].quantity, 10);
      latestCartLineQty[String(i + 1)] = isNaN(q) || q < 0 ? 0 : q;
    }
  }

  function readLineQty(row, rowIndex) {
    var lineId = row ? String(row.getAttribute('data-line-id') || '').trim() : '';
    if (lineId && latestCartLineQty[lineId] != null) {
      return latestCartLineQty[lineId];
    }
    var input = row.querySelector('input[name="updates[]"], input.quantity');
    if (!input) return 1;
    var qty = parseInt(input.value, 10);
    return isNaN(qty) || qty < 1 ? 1 : qty;
  }

  function readDefaultUnitCents(unitEl) {
    var attr = unitEl.getAttribute('data-default-cents');
    if (attr != null && attr !== '') {
      var cents = parseInt(attr, 10);
      if (!isNaN(cents)) return cents;
    }
    ensureDefaultDisplay(unitEl);
    return parseMoneyTextToCents(unitEl.getAttribute('data-default-display') || unitEl.textContent);
  }

  function cartPricingRows() {
    var selector = '[data-cart-item][data-variant-id]:not([data-aboffs-additional-fees])';
    var cartTemplateForm = document.querySelector('#cart_form[data-cart-form="cart-template"]');
    if (cartTemplateForm) {
      var templateRows = cartTemplateForm.querySelectorAll(selector);
      if (templateRows.length) return templateRows;
    }
    var miniCartForm = document.querySelector('[data-cart-form="mini-cart"]');
    if (miniCartForm) {
      return miniCartForm.querySelectorAll(selector);
    }
    return document.querySelectorAll(selector);
  }

  function dedupePricingEntries(entries) {
    if (!entries || !entries.length) return [];
    var seen = Object.create(null);
    var result = [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var lineId = entry.lineId != null ? String(entry.lineId) : '';
      var variantId = entry.variantId != null ? String(entry.variantId) : '';
      var key = variantId + '|' + lineId;
      if (seen[key]) continue;
      seen[key] = true;
      result.push(entry);
    }
    return result;
  }

  function cartRowsForLine(variantId, lineId) {
    if (!variantId) return [];
    var selector =
      '[data-cart-item][data-variant-id="' +
      String(variantId).replace(/"/g, '\\"') +
      '"]:not([data-aboffs-additional-fees])';
    if (lineId) {
      selector =
        '[data-cart-item][data-variant-id="' +
        String(variantId).replace(/"/g, '\\"') +
        '"][data-line-id="' +
        String(lineId).replace(/"/g, '\\"') +
        '"]:not([data-aboffs-additional-fees])';
    }
    return document.querySelectorAll(selector);
  }

  function applyCartLinePricingToRow(row, payload, state, qty, defaultUnitCents, useOverride, cartItem) {
    if (!row || !row.isConnected) return;
    var unitEl = row.querySelector('.js-cart-unit-price');
    if (!unitEl) return;

    ensureDefaultDisplay(unitEl);
    var lineEl = row.querySelector('.js-cart-line-total');
    if (lineEl) ensureDefaultDisplay(lineEl);

    row.classList.remove('aboffs-pricing--hidden');
    row.removeAttribute('aria-hidden');
    row.style.display = '';

    var unitCents =
      useOverride && payload
        ? cartDisplayUnitCentsFromPayload(payload)
        : defaultUnitCents;
    if (unitCents == null) unitCents = defaultUnitCents;
    var displayUnitCents = discountedUnitCentsForCartItem(unitCents, cartItem);
    var unitDisplay =
      useOverride && payload
        ? formatMoneyCents(displayUnitCents)
        : unitEl.getAttribute('data-default-display');
    var lineOriginalCents = unitCents * qty;
    var lineBaseCents = discountedLineCentsForCartItem(lineOriginalCents, cartItem);
    var lineDisplay = formatMoneyCents(lineBaseCents);
    var priceScope = unitEl.closest('.modal_price, .price, .right.price');
    var compareEl = priceScope ? priceScope.querySelector('.js-cart-unit-compare') : null;
    var hasUnitDiscount = displayUnitCents < unitCents;
    var hasLineDiscount = lineBaseCents < lineOriginalCents;
    var priceTotalWrap = lineEl ? lineEl.closest('.price_total') : null;

    if (hasUnitDiscount) {
      if (!compareEl && priceScope) {
        compareEl = document.createElement('span');
        compareEl.className = 'money js-cart-unit-compare cart__item--compare-at';
        priceScope.insertBefore(compareEl, unitEl);
      }
      if (compareEl) {
        compareEl.textContent = formatMoneyCents(unitCents);
        compareEl.style.display = '';
      }
      unitEl.textContent = unitDisplay || '';
      if (unitEl.classList) unitEl.classList.add('sale');
    } else {
      if (compareEl) compareEl.style.display = 'none';
      unitEl.textContent = unitDisplay || '';
      if (unitEl.classList) unitEl.classList.remove('sale');
    }
    if (priceTotalWrap) {
      var legacyLineTotals = priceTotalWrap.querySelectorAll(
        '.money:not(.js-cart-line-total):not(.js-cart-line-compare)'
      );
      for (var lt = 0; lt < legacyLineTotals.length; lt++) {
        legacyLineTotals[lt].style.display = 'none';
      }
    }
    if (lineEl) {
      var lineCompareEl = priceTotalWrap ? priceTotalWrap.querySelector('.js-cart-line-compare') : null;
      if (hasLineDiscount) {
        if (!lineCompareEl && priceTotalWrap) {
          lineCompareEl = document.createElement('span');
          lineCompareEl.className = 'money js-cart-line-compare cart__item--compare-at';
          priceTotalWrap.insertBefore(lineCompareEl, lineEl);
        }
        if (lineCompareEl) {
          lineCompareEl.textContent = formatMoneyCents(lineOriginalCents);
          lineCompareEl.style.display = '';
        }
        lineEl.textContent = lineDisplay;
        if (lineEl.classList) lineEl.classList.add('sale');
      } else {
        if (lineCompareEl) lineCompareEl.style.display = 'none';
        lineEl.textContent = lineDisplay;
        if (lineEl.classList) lineEl.classList.remove('sale');
      }
    }
    renderCartLineDiscountNote(row, cartItem, useOverride && payload ? unitCents : null, qty);
    setCartCompareAtVisible(row, !useOverride);
    revealHydratedPrice(unitEl);
    if (lineEl) revealHydratedPrice(lineEl);
  }

  function applyCartLikePrices(state) {
    var rows = cartPricingRows();
    var subtotalEls = document.querySelectorAll('.js-cart-subtotal-money');
    clearAllCartFeeItems();
    ensureAdditionalFeesContainers();
    if (!rows.length) {
      renderConsolidatedCartFees(null);
      renderCartDiscounts(null);
      for (var e = 0; e < subtotalEls.length; e++) {
        subtotalEls[e].textContent = formatMoneyCents(0);
        revealHydratedPrice(subtotalEls[e]);
      }
      return;
    }

    fetchCartJson()
      .catch(function () {
        return null;
      })
      .then(function (pricingCart) {
        renderCartDiscounts(pricingCart);

        var pending = 0;
        var productSubtotalCents = 0;
        var pricingEntries = [];
        var seenRows = Object.create(null);

        function applyCartSubtotal(feeSubtotalCents) {
          var orderDiscountCents = cartLevelDiscountCents(pricingCart);
          var cartSubtotalCents = Math.max(0, productSubtotalCents + feeSubtotalCents - orderDiscountCents);
          for (var s = 0; s < subtotalEls.length; s++) {
            subtotalEls[s].textContent = formatMoneyCents(cartSubtotalCents);
            revealHydratedPrice(subtotalEls[s]);
          }
        }

        function finish() {
          pending--;
          if (pending > 0) return;
          var overrideSavingsCents = computeOverrideCartSavingsCents(pricingEntries, pricingCart);
          renderCartSavings(pricingCart, overrideSavingsCents);
          var dedupedEntries = dedupePricingEntries(pricingEntries);
          var fixedFees = buildConsolidatedCartFees(dedupedEntries, state);
          var feeRequestLines = buildCartFeesRequestLines(dedupedEntries);
          var applyMergedFees = function (apiPercentageFees) {
            var percentageFees = resolveCartPercentageFees(
              dedupedEntries,
              pricingCart,
              apiPercentageFees,
              state
            );
            var mergedFees = mergeCartFeeDisplays(fixedFees, percentageFees);
            renderConsolidatedCartFees(mergedFees);
            var feeSubtotalCents = mergedFees ? mergedFees.totalCents : 0;
            applyCartSubtotal(feeSubtotalCents);
          };
          if (!feeRequestLines.length) {
            applyMergedFees(null);
            return;
          }
          var discountCodes = discountCodesFromCart(pricingCart);
          fetchCartFees(feeRequestLines, state, discountCodes)
            .then(function (data) {
              return percentageFeesFromCartFeesResponse(data);
            })
            .catch(function () {
              return null;
            })
            .then(applyMergedFees)
            .catch(function () {
              applyMergedFees(null);
            });
        }

        for (var i = 0; i < rows.length; i++) {
          (function (row, rowIndex) {
            var variantId = row.getAttribute('data-variant-id');
            if (!variantId) return;

            var unitEl = row.querySelector('.js-cart-unit-price');
            var lineEl = row.querySelector('.js-cart-line-total');
            if (!unitEl) return;

            ensureDefaultDisplay(unitEl);
            if (lineEl) ensureDefaultDisplay(lineEl);
            hidePriceUntilHydrated(unitEl);
            if (lineEl) hidePriceUntilHydrated(lineEl);

            var qty = readLineQty(row, rowIndex);
            var lineId = row.getAttribute('data-line-id') || '';
            var dedupeKey = lineId ? String(variantId) + '|' + String(lineId) : String(variantId) + '|row:' + String(rowIndex);
            var shouldAddToSubtotal = !seenRows[dedupeKey];
            seenRows[dedupeKey] = true;
            var defaultUnitCents = readDefaultUnitCents(unitEl);
            var cartItem = cartItemForRow(pricingCart, row);
            pending++;

            fetchOverridePrice(variantId, state)
              .then(function (payload) {
                return payloadForSaleTemplateProduct(
                  payload,
                  cartItem && cartItem.handle,
                  variantId
                );
              })
              .then(function (payload) {
                if (payload && payload.isVisible === false) {
                  payload = null;
                }
                var useOverride = payload && payload.unitCents != null && payload.display;
                var unitCents =
                  useOverride && payload
                    ? cartDisplayUnitCentsFromPayload(payload)
                    : defaultUnitCents;
                if (unitCents == null) unitCents = defaultUnitCents;
                var lineBaseCents = discountedLineCentsForCartItem(unitCents * qty, cartItem);
                var mirrorRows = cartRowsForLine(variantId, lineId);
                for (var m = 0; m < mirrorRows.length; m++) {
                  applyCartLinePricingToRow(
                    mirrorRows[m],
                    payload,
                    state,
                    qty,
                    defaultUnitCents,
                    useOverride,
                    cartItemForRow(pricingCart, mirrorRows[m]) || cartItem
                  );
                }
                pricingEntries.push({
                  row: row,
                  lineId: lineId,
                  variantId: variantId,
                  quantity: qty,
                  variantLabel: cartVariantLabelFromRow(row),
                  defaultUnitCents: defaultUnitCents,
                  discountedUnitCents: discountedUnitCentsForFeeRequest(unitCents, cartItem),
                  payload: useOverride ? payload : null
                });
                if (useOverride && payload) {
                  row.setAttribute('data-aboffs-base-unit-cents', String(payload.baseUnitCents || ''));
                  row.setAttribute('data-aboffs-checkout-unit-cents', String(payload.checkoutUnitCents || ''));
                }
                if (shouldAddToSubtotal) {
                  productSubtotalCents += lineBaseCents;
                }
              })
              .catch(function () {
                var mirrorRows = cartRowsForLine(variantId, lineId);
                for (var m = 0; m < mirrorRows.length; m++) {
                  applyCartLinePricingToRow(
                    mirrorRows[m],
                    null,
                    state,
                    qty,
                    defaultUnitCents,
                    false,
                    cartItemForRow(pricingCart, mirrorRows[m]) || cartItem
                  );
                }
                pricingEntries.push({
                  row: row,
                  lineId: lineId,
                  variantId: variantId,
                  quantity: qty,
                  variantLabel: cartVariantLabelFromRow(row),
                  defaultUnitCents: defaultUnitCents,
                  discountedUnitCents: discountedUnitCentsForFeeRequest(defaultUnitCents, cartItem),
                  payload: null
                });
                if (shouldAddToSubtotal) {
                  productSubtotalCents += discountedLineCentsForCartItem(defaultUnitCents * qty, cartItem);
                }
              })
              .finally(finish);
          })(rows[i], i);
        }

        if (pending === 0) {
          var emptyOverrideSavingsCents = computeOverrideCartSavingsCents(pricingEntries, pricingCart);
          renderCartSavings(pricingCart, emptyOverrideSavingsCents);
          var emptyDedupedEntries = dedupePricingEntries(pricingEntries);
          var emptyFixedFees = buildConsolidatedCartFees(emptyDedupedEntries, state);
          var emptyFeeRequestLines = buildCartFeesRequestLines(emptyDedupedEntries);
          var applyEmptyMergedFees = function (apiPercentageFees) {
            var percentageFees = resolveCartPercentageFees(
              emptyDedupedEntries,
              pricingCart,
              apiPercentageFees,
              state
            );
            var emptyMergedFees = mergeCartFeeDisplays(emptyFixedFees, percentageFees);
            renderConsolidatedCartFees(emptyMergedFees);
            var emptyFeeSubtotalCents = emptyMergedFees ? emptyMergedFees.totalCents : 0;
            applyCartSubtotal(emptyFeeSubtotalCents);
          };
          if (!emptyFeeRequestLines.length) {
            applyEmptyMergedFees(null);
          } else {
            fetchCartFees(emptyFeeRequestLines, state, discountCodesFromCart(pricingCart))
              .then(function (data) {
                return percentageFeesFromCartFeesResponse(data);
              })
              .catch(function () {
                return null;
              })
              .then(applyEmptyMergedFees);
          }
        }
      });
  }

  function preHidePriceNodesInScope(root) {
    if (!root || !root.querySelectorAll) return;
    var nodes = root.querySelectorAll(
      '.js-price-override[data-variant-id], .modal_price .current_price, .js-search-compare-at'
    );
    for (var i = 0; i < nodes.length; i++) {
      markNodePendingForReprice(nodes[i]);
    }
  }

  function preHideDirectPriceNodes() {
    var nodes = document.querySelectorAll('.js-price-override[data-variant-id]');
    for (var i = 0; i < nodes.length; i++) {
      markNodePendingForReprice(nodes[i]);
    }

    // Search "was" prices also need pre-hide to avoid a flash of default
    // compare-at value before override hydration finishes.
    var searchCompareNodes = document.querySelectorAll('.js-search-compare-at');
    for (var s = 0; s < searchCompareNodes.length; s++) {
      markNodePendingForReprice(searchCompareNodes[s]);
    }

    var fallbackNodes = document.querySelectorAll('.modal_price .current_price:not(.js-price-override)');
    for (var j = 0; j < fallbackNodes.length; j++) {
      var el = fallbackNodes[j];
      var scope = el.closest('.product_section, .product-quickshop, .js-product_section') || document;
      var idField = scope.querySelector('select[name="id"], input[name="id"]');
      if (!idField || !idField.value) continue;
      markNodePendingForReprice(el);
    }

    // Cart templates render default Shopify prices server-side first.
    // Pre-hide these nodes before we release the global mask to avoid a flash
    // of default price while override pricing is fetched.
    var cartNodes = document.querySelectorAll('.js-cart-subtotal-money, .js-cart-unit-price, .js-cart-line-total');
    for (var k = 0; k < cartNodes.length; k++) {
      markNodePendingForReprice(cartNodes[k]);
    }
  }

  function markNodePendingForReprice(el) {
    if (!el) return;
    ensureDefaultDisplay(el);
    if (el.classList && el.classList.add) {
      el.classList.add('price-mask-pending');
    }
    el.removeAttribute('data-price-hydrated');
    el.removeAttribute('data-price-pending');
    hidePriceUntilHydrated(el);
  }

  function primeProductPriceMask(target) {
    if (!target || !target.closest) return;
    var scope = findProductPricingScope(target);
    if (!scope) {
      scope = target.closest('.product_section, .product-quickshop, .js-product_section, .js-quick-shop') || document;
    }
    var nodes = scope.querySelectorAll('.js-price-override[data-variant-id], .modal_price .current_price');
    for (var i = 0; i < nodes.length; i++) {
      markNodePendingForReprice(nodes[i]);
    }
  }

  function isVariantChangeTarget(target) {
    if (!target) return false;
    return (
      target.name === 'id' ||
      (target.matches && target.matches('select[name="id"], input[name="id"], .single-option-selector'))
    );
  }

  function isCartTemplateInteractionTarget(target) {
    if (!target || !target.closest) return false;
    return !!target.closest('#cart_form[data-cart-form="cart-template"], [data-cart-form="cart-template"]');
  }

  function releaseInitialPriceMask() {
    var root = document.documentElement;
    if (!root || !root.classList || !root.classList.contains('price-mask-init')) return;
    root.classList.remove('price-mask-init');
  }

  function runApply(options) {
    options = normalizeApplyOptions(options);
    if (applyInProgress) {
      applyQueued = true;
      applyQueuedOptions = mergeApplyOptions(applyQueuedOptions, options);
      return;
    }
    applyInProgress = true;
    applyQueued = false;
    applyQueuedOptions = null;
    if (options.scope === 'product' && options.root) {
      preHidePriceNodesInScope(options.root);
    } else if (options.scope === 'full') {
      clearOverridePriceCache();
      preHideDirectPriceNodes();
    }
    releaseInitialPriceMask();
    resolveStateCode()
      .then(function (state) {
        if (options.scope === 'full') {
          applyLocationBranding(state);
        }
        if (options.scope === 'full' || options.scope === 'cart') {
          applyCartLikePrices(state);
        }
        return applyDirectPriceNodes(state, options);
      })
      .finally(function () {
        dispatchSearchSuggestionsReady();
        applyInProgress = false;
        if (applyQueued) {
          applyQueued = false;
          var nextOptions = applyQueuedOptions || { scope: 'full' };
          applyQueuedOptions = null;
          runApply(nextOptions);
        }
      });
  }

  function scheduleApply(options) {
    pendingApplyOptions = mergeApplyOptions(pendingApplyOptions, options);
    if (applyTimer) return;
    applyTimer = setTimeout(function () {
      applyTimer = null;
      var now = Date.now();
      var opts = pendingApplyOptions || { scope: 'full' };
      pendingApplyOptions = null;
      if (now - lastApplyAt < APPLY_MIN_INTERVAL_MS) return;
      lastApplyAt = now;
      runApply(opts);
    }, 80);
  }

  function scheduleApplyImmediate(options) {
    pendingApplyOptions = mergeApplyOptions(pendingApplyOptions, options);
    if (applyTimer) {
      clearTimeout(applyTimer);
      applyTimer = null;
    }
    lastApplyAt = Date.now();
    var opts = pendingApplyOptions || { scope: 'full' };
    pendingApplyOptions = null;
    if (applyInProgress) {
      applyQueued = true;
      applyQueuedOptions = mergeApplyOptions(applyQueuedOptions, opts);
      return;
    }
    runApply(opts);
  }

  function dispatchSearchSuggestionsReady() {
    if (typeof document === 'undefined' || !document.dispatchEvent) return;
    var evt;
    try {
      evt = new CustomEvent(SEARCH_SUGGESTIONS_READY_EVENT);
    } catch (err) {
      evt = document.createEvent('Event');
      evt.initEvent(SEARCH_SUGGESTIONS_READY_EVENT, true, true);
    }
    document.dispatchEvent(evt);
  }

  function findTosCheckbox(root) {
    if (!root) return null;
    return root.querySelector('.tos_agree[required], input.tos_agree[required]');
  }

  function findCartNote(root) {
    var scope = root || document;
    var noteEl =
      scope.querySelector('textarea[name="note"]') ||
      document.querySelector('textarea[name="note"]') ||
      document.getElementById('note');
    return noteEl && noteEl.value ? String(noteEl.value).trim() : '';
  }

  function readCartAttributesFromForm(root) {
    var scope = root || document;
    if (!scope || !scope.querySelectorAll) return {};
    var attrs = {};
    var fields = scope.querySelectorAll('input[name^="attributes["], textarea[name^="attributes["], select[name^="attributes["]');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (!field || field.disabled || typeof field.name !== 'string') continue;
      var match = field.name.match(/^attributes\[(.+)\]$/);
      if (!match || !match[1]) continue;
      attrs[match[1]] = field.value == null ? '' : String(field.value);
    }
    return attrs;
  }

  function toCustomAttributesArray(attributes) {
    if (!attributes || typeof attributes !== 'object') return [];
    var customAttributes = [];
    for (var key in attributes) {
      if (!Object.prototype.hasOwnProperty.call(attributes, key)) continue;
      var value = attributes[key];
      if (value == null) continue;
      var asString = String(value).trim();
      if (!asString) continue;
      customAttributes.push({
        key: String(key),
        value: asString
      });
    }
    return customAttributes;
  }

  function feeAttributeKeyForFee(feeName, stateCode) {
    return feeLabelWithLocation(feeName, stateCode);
  }

  function feeAttributeKeyForPricing(pricing) {
    if (!pricing || !pricing.hasAssignedFee) return '';
    return feeAttributeKeyForFee(pricing.feeName || 'Fee', pricing.state || pricing.stateCode);
  }

  function feeCustomAttributesForLine(pricing, quantity) {
    if (!pricing || !pricing.hasAssignedFee) return [];
    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    var stateCode = pricing.state || pricing.stateCode || '';
    var attrs = [];

    if (pricing.fees && pricing.fees.length) {
      for (var i = 0; i < pricing.fees.length; i++) {
        var fee = pricing.fees[i];
        if (!fee.cents) continue;
        var key = feeAttributeKeyForFee(fee.name, stateCode);
        if (!key) continue;
        var perUnit = fee.displayAmount;
        var value =
          qty > 1
            ? perUnit + ' x ' + qty + ' = ' + formatMoneyCents(fee.cents * qty)
            : perUnit;
        attrs.push({ key: key, value: value });
      }
      return attrs;
    }

    if (!pricing.feeCents) return [];
    var legacyKey = feeAttributeKeyForPricing(pricing);
    if (!legacyKey) return [];
    var legacyPerUnit = formatMoneyCents(pricing.feeCents);
    var legacyValue =
      qty > 1
        ? legacyPerUnit + ' x ' + qty + ' = ' + formatMoneyCents(pricing.feeCents * qty)
        : legacyPerUnit;
    return [{ key: legacyKey, value: legacyValue }];
  }

  function feePropertiesForLine(pricing, quantity) {
    var attrs = feeCustomAttributesForLine(pricing, quantity);
    if (!attrs.length) return {};
    var props = {};
    for (var i = 0; i < attrs.length; i++) {
      props[attrs[i].key] = attrs[i].value;
    }
    return props;
  }

  function feeAttributeValueForCents(cents, quantity) {
    var qty = parseInt(quantity, 10);
    if (isNaN(qty) || qty < 1) qty = 1;
    var perUnit = formatMoneyCents(cents);
    return qty > 1 ? perUnit + ' x ' + qty + ' = ' + formatMoneyCents(cents * qty) : perUnit;
  }

  function feeItemsForPricing(pricing) {
    if (!pricing || !pricing.hasAssignedFee) return [];
    if (Array.isArray(pricing.fees) && pricing.fees.length) return pricing.fees;
    if (pricing.feeCents) {
      return [{ id: '', name: pricing.feeName || 'Fee', cents: pricing.feeCents }];
    }
    return [];
  }

  function feeAttributeKeysForPricing(pricing) {
    var keys = [];
    var stateCode = pricing.state || pricing.stateCode || '';
    var feeItems = feeItemsForPricing(pricing);
    for (var i = 0; i < feeItems.length; i++) {
      var key = feeAttributeKeyForFee(feeItems[i].name, stateCode);
      if (key) keys.push(key);
    }
    return keys;
  }

  function stripFeeMetadataFromLine(line) {
    if (!line || !line.pricing || !line.pricing.hasAssignedFee) return;
    var feeKeys = feeAttributeKeysForPricing(line.pricing);
    if (!feeKeys.length) return;

    var existingAttrs = line.customAttributes || [];
    line.customAttributes = existingAttrs.filter(function (attr) {
      return attr && feeKeys.indexOf(attr.key) === -1;
    });

    var props = Object.assign({}, line.properties || {});
    for (var i = 0; i < feeKeys.length; i++) {
      delete props[feeKeys[i]];
    }
    line.properties = props;
  }

  function cartItemLabel(cartItem) {
    if (!cartItem) return '';
    var productTitle = String(cartItem.product_title || cartItem.title || '').trim();
    var variantTitle = String(cartItem.variant_title || '').trim();
    if (productTitle && variantTitle) return productTitle + ' - ' + variantTitle;
    return productTitle || variantTitle;
  }

  function checkoutFeeLineTitle(fee, cartItem) {
    var productTitle = String((cartItem && cartItem.product_title) || '').trim();
    var feeLabel = (fee && (fee.label || fee.name)) || 'Fee';
    return feeCartItemTitle(feeLabel, productTitle);
  }

  function buildDraftCheckoutFeeLines(lines, cartItems) {
    var feeLines = [];
    if (!lines || !lines.length) return feeLines;

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var pricing = line && line.pricing;
      if (!pricing || !pricing.hasAssignedFee) continue;

      var qty = parseInt(line.quantity, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      var cartItem = cartItems && cartItems[i];
      var feeItems = feeItemsForPricing(pricing);
      var stateCode = pricing.state || pricing.stateCode || '';

      for (var j = 0; j < feeItems.length; j++) {
        var fee = feeItems[j];
        if (!fee || !fee.cents) continue;
        var feeAttrKey = feeAttributeKeyForFee(fee.name || fee.label || 'Fee', stateCode);
        feeLines.push({
          title: checkoutFeeLineTitle(fee, cartItem),
          originalUnitPriceCents: fee.cents,
          quantity: qty,
          variantId: line.variantId,
          feeId: fee.id || '',
          requiresShipping: false,
          taxable: false,
          customAttributes: feeAttrKey
            ? [{ key: feeAttrKey, value: feeAttributeValueForCents(fee.cents, qty) }]
            : []
        });
      }
    }

    return feeLines;
  }

  function draftCheckoutFeeEntries(pricedLines) {
    if (!pricedLines || !pricedLines.length) return [];
    var entries = [];
    for (var i = 0; i < pricedLines.length; i++) {
      var line = pricedLines[i];
      if (!line || !line.pricing) continue;
      var lineIndex = i;
      entries.push({
        row: {
          getAttribute: function (name) {
            return name === 'data-line-id' ? String(lineIndex + 1) : null;
          }
        },
        lineId: String(lineIndex + 1),
        variantId: line.variantId,
        quantity: line.quantity || 1,
        payload: {
          baseUnitCents: line.pricing.baseUnitCents,
          unitCents:
            line.pricing.baseUnitCents != null ? line.pricing.baseUnitCents : line.pricing.unitCents,
          percentageFees: line.pricing.percentageFees,
          fees: line.pricing.fees
        }
      });
    }
    return entries;
  }

  function applyOverridePercentageFeesToDraftLines(pricedLines, cartItems, apiPercentageFees, stateCode) {
    if (!pricedLines || !pricedLines.length) return pricedLines;
    var entries = draftCheckoutFeeEntries(pricedLines);
    var cart = { items: cartItems || [] };
    var overrideFees = resolveCartPercentageFees(entries, cart, apiPercentageFees, stateCode);
    if (!overrideFees || !overrideFees.breakdown || !overrideFees.breakdown.length) return pricedLines;

    var totalOverrideBase = overridePercentageFeeLineBaseCents(entries, cart);

    for (var i = 0; i < pricedLines.length; i++) {
      var line = pricedLines[i];
      var pricing = line && line.pricing;
      if (!pricing || !pricing.hasPercentageFee) continue;

      var cartItem = cartItems && cartItems[i];
      var unitCents = pricing.baseUnitCents != null ? pricing.baseUnitCents : pricing.unitCents;
      if (unitCents == null) continue;
      var qty = Math.max(1, parseInt(line.quantity, 10) || 1);
      var lineBase = discountedUnitCentsForCartItem(unitCents, cartItem);
      if (lineBase == null) lineBase = unitCents;
      lineBase *= qty;
      var lineShare = totalOverrideBase > 0 ? lineBase / totalOverrideBase : 1;

      var fixedFees = pricing.fixedFees && pricing.fixedFees.length
        ? pricing.fixedFees.slice()
        : fixedFeesFromPayload(pricing.fees || []);

      var linePctFees = [];
      for (var j = 0; j < overrideFees.breakdown.length; j++) {
        var pctRow = overrideFees.breakdown[j];
        var lineTotalCents =
          pricedLines.length > 1 && totalOverrideBase > 0
            ? Math.round((pctRow.cents || 0) * lineShare)
            : pctRow.cents || 0;
        if (lineTotalCents <= 0) continue;
        var perUnitCents = Math.max(1, Math.round(lineTotalCents / qty));
        linePctFees.push({
          name: pctRow.name || 'Percentage fee',
          type: 'PERCENTAGE',
          percentage: pctRow.percentage || '',
          cents: perUnitCents,
          displayAmount: formatMoneyCents(perUnitCents),
          label: pctRow.label || pctRow.name || 'Percentage fee'
        });
      }

      if (!linePctFees.length) continue;
      pricing.percentageFees = linePctFees;
      pricing.percentageFeeCents = linePctFees.reduce(function (sum, fee) {
        return sum + (fee.cents || 0) * qty;
      }, 0);
      pricing.fees = fixedFees.concat(linePctFees);
      pricing.feeTotalCents = (pricing.fixedFeeCents || 0) + pricing.percentageFeeCents;
      pricing.hasAssignedFee = pricing.hasFixedFee || pricing.hasPercentageFee;
    }

    return pricedLines;
  }

  function syncCartFormState(form) {
    if (!form || !form.tagName || String(form.tagName).toLowerCase() !== 'form') {
      return Promise.resolve();
    }
    var formData = new FormData(form);
    var payload = new URLSearchParams();
    var hasSyncableFields = false;
    formData.forEach(function (value, key) {
      if (typeof key !== 'string') return;
      if (key === 'note' || key === 'updates[]' || key.indexOf('attributes[') === 0) {
        payload.append(key, value == null ? '' : String(value));
        hasSyncableFields = true;
      }
    });
    if (!hasSyncableFields) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
      },
      credentials: 'same-origin',
      body: payload.toString()
    }).then(function () {
      return null;
    });
  }

  function pricingUnavailableError() {
    var err = new Error('One or more items are unavailable in your location.');
    err.code = 'STATE_PRICING_UNAVAILABLE';
    return err;
  }

  function pricingFetchError(cause) {
    var err = new Error('Unable to load pricing for checkout. Please try again.');
    err.code = 'STATE_PRICING_FETCH_FAILED';
    if (cause) err.cause = cause;
    return err;
  }

  function cartAttributeValue(attributes, key) {
    if (!attributes || typeof attributes !== 'object') return '';
    if (attributes[key] != null && String(attributes[key]).trim()) {
      return String(attributes[key]).trim();
    }
    var target = String(key || '').toLowerCase();
    for (var attrKey in attributes) {
      if (!Object.prototype.hasOwnProperty.call(attributes, attrKey)) continue;
      if (String(attrKey).toLowerCase() !== target) continue;
      var value = attributes[attrKey];
      if (value != null && String(value).trim()) return String(value).trim();
    }
    return '';
  }

  function zapietCheckoutNotReadyError(message) {
    var err = new Error(message || 'Please complete pickup, delivery, or shipping selection before checkout.');
    err.code = 'ZAPIET_CHECKOUT_REQUIRED';
    return err;
  }

  function validateZapietCheckoutAttributes(attributes) {
    var method = cartAttributeValue(attributes, ZAPIET_CHECKOUT_METHOD_ATTR).toLowerCase();
    if (!method) {
      return {
        ok: false,
        message: 'Please choose pickup, delivery, or shipping above before checkout.'
      };
    }
    if (method === 'pickup') {
      if (!cartAttributeValue(attributes, 'Pickup-Location-Id')) {
        return {
          ok: false,
          message: 'Please select a pickup location before checkout.'
        };
      }
      if (!cartAttributeValue(attributes, 'Pickup-Date') || !cartAttributeValue(attributes, 'Pickup-Time')) {
        return {
          ok: false,
          message: 'Please select a pickup date and time before checkout.'
        };
      }
    } else if (method === 'delivery') {
      if (!cartAttributeValue(attributes, 'Delivery-Location-Id')) {
        return {
          ok: false,
          message: 'Please confirm your delivery address before checkout.'
        };
      }
      if (!cartAttributeValue(attributes, 'Delivery-Date') || !cartAttributeValue(attributes, 'Delivery-Time')) {
        return {
          ok: false,
          message: 'Please select a delivery date and time before checkout.'
        };
      }
    } else if (method === 'shipping') {
      if (!cartAttributeValue(attributes, 'Shipping-Date')) {
        return {
          ok: false,
          message: 'Please select a shipping date before checkout.'
        };
      }
    } else {
      return {
        ok: false,
        message: 'Please complete your delivery method selection before checkout.'
      };
    }
    return { ok: true, method: method };
  }

  function focusZapietWidget() {
    var widget = document.getElementById('storePickupApp');
    if (!widget) return;
    try {
      widget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (e) {
      widget.scrollIntoView();
    }
    var focusable = widget.querySelector('input, select, textarea, button, [tabindex]');
    if (focusable && focusable.focus) focusable.focus();
  }

  function showZapietCheckoutNotice(message) {
    var text = String(message || '').trim();
    if (!text) return;
    var notice = document.getElementById('aboffs-zapiet-notice');
    if (notice) {
      notice.textContent = text;
      notice.hidden = false;
      notice.style.display = '';
      return;
    }
    alert(text);
  }

  function clearZapietCheckoutNotice() {
    var notice = document.getElementById('aboffs-zapiet-notice');
    if (!notice) return;
    notice.textContent = '';
    notice.hidden = true;
    notice.style.display = 'none';
  }

  function copyLineItemProperties(properties) {
    return properties && typeof properties === 'object' ? Object.assign({}, properties) : {};
  }

  function buildDraftCheckoutLinesWithPricing(lines, state, cartItems) {
    if (!lines || !lines.length) return Promise.resolve(lines || []);
    var normalizedState = normalizeStateCode(state || '');
    return Promise.all(
      lines.map(function (line, index) {
        var variantId = line && line.variantId;
        if (!variantId) return Promise.resolve(line);
        var cartItem = cartItems && cartItems[index];
        var discountedUnitCents =
          line.discountedUnitCents != null
            ? line.discountedUnitCents
            : cartItem && cartItem.final_price != null
              ? cartItem.final_price
              : null;
        return fetchOverridePrice(variantId, normalizedState)
          .then(function (payload) {
            return payloadForSaleTemplateProduct(
              payload,
              cartItem && cartItem.handle,
              variantId
            );
          })
          .then(function (payload) {
            if (payload && payload.isVisible === false) {
              throw pricingUnavailableError();
            }
            if (!line || !payload) return line;
            var checkoutUnitCents =
              payload.checkoutUnitCents != null
                ? payload.checkoutUnitCents
                : payload.baseUnitCents != null
                  ? payload.baseUnitCents + (payload.fixedFeeCents || 0)
                  : payload.unitCents;
            var productUnitCents = cartDisplayUnitCentsFromPayload(payload);
            if (productUnitCents == null) productUnitCents = checkoutUnitCents;
            var splitProductPrice = draftLineHasSplittableFees(payload);
            var lineProductUnitCents = splitProductPrice
              ? splitDraftCheckoutProductUnitCents(
                  {
                    baseUnitCents: payload.baseUnitCents,
                    unitCents: productUnitCents
                  },
                  cartItem
                )
              : discountedUnitCentsForCartItem(checkoutUnitCents, cartItem);
            if (lineProductUnitCents == null) lineProductUnitCents = productUnitCents;
            line.pricing = {
              state: normalizedState,
              stateCode: payload.stateCode || normalizedState,
              baseUnitCents: payload.baseUnitCents == null ? null : payload.baseUnitCents,
              unitCents: lineProductUnitCents,
              checkoutUnitCents: splitProductPrice ? lineProductUnitCents : checkoutUnitCents,
              discountedUnitCents: lineProductUnitCents,
              fixedFeeCents: payload.fixedFeeCents || 0,
              percentageFeeCents: payload.percentageFeeCents || 0,
              feeCents: payload.fixedFeeCents || 0,
              feeName: payload.feeName || '',
              fixedFees: payload.fixedFees || fixedFeesFromPayload(payload.fees || []),
              percentageFees: payload.percentageFees || percentageFeesFromPayload(payload.fees || []),
              fees: payload.fixedFees && payload.fixedFees.length ? payload.fixedFees : payload.fees || [],
              hasFixedFee: !!payload.hasFixedFee,
              hasPercentageFee: !!payload.hasPercentageFee,
              hasAssignedFee: !!payload.hasAssignedFee
            };
            if (splitProductPrice) {
              stripFeeMetadataFromLine(line);
              line.originalUnitPriceCents = lineProductUnitCents;
              line.discountedUnitCents = lineProductUnitCents;
            } else {
              var feeAttrs = feeCustomAttributesForLine(line.pricing, line.quantity);
              var existingAttrs = line.customAttributes || [];
              var feeKeys = feeAttrs.map(function (a) {
                return a.key;
              });
              line.customAttributes = existingAttrs
                .filter(function (attr) {
                  return attr && feeKeys.indexOf(attr.key) === -1;
                })
                .concat(feeAttrs);
              line.properties = Object.assign(
                {},
                line.properties || {},
                feePropertiesForLine(line.pricing, line.quantity)
              );
            }
            return line;
          })
          .catch(function (err) {
            if (err && err.code === 'STATE_PRICING_UNAVAILABLE') throw err;
            throw pricingFetchError(err);
          });
      })
    );
  }

  function createDraftCheckout(sourceForm) {
    return syncCartFormState(sourceForm).then(function () {
      return fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
    })
      .then(function (cartRes) {
        if (!cartRes.ok) throw new Error('Failed to load cart');
        return cartRes.json();
      })
      .then(function (cart) {
        var formAttributes = readCartAttributesFromForm(sourceForm || document);
        var mergedAttributes = Object.assign({}, cart.attributes || {}, formAttributes);
        var zapietValidation = validateZapietCheckoutAttributes(mergedAttributes);
        if (!zapietValidation.ok) {
          throw zapietCheckoutNotReadyError(zapietValidation.message);
        }
        if (
          typeof window.__aboffsPickupNoSlotsBlocksCheckout === 'function' &&
          window.__aboffsPickupNoSlotsBlocksCheckout()
        ) {
          throw zapietCheckoutNotReadyError(
            'No pickup times are available for this date. Please choose another date.'
          );
        }
        var customAttributes = toCustomAttributesArray(mergedAttributes);
        var discountCodes = discountCodesFromCart(cart);
        var lines = (cart.items || []).map(function (item) {
          return {
            variantId: item.variant_id,
            quantity: item.quantity,
            discountedUnitCents: item.final_price,
            properties: copyLineItemProperties(item.properties || {})
          };
        });
        if (!lines.length) throw new Error('Your cart is empty');

        return resolveStateCode().then(function (state) {
          var url = appUrl(DRAFT_CHECKOUT_PATH) + '?ts=' + Date.now();
          if (state) url += '&state=' + encodeURIComponent(state);
          return buildDraftCheckoutLinesWithPricing(lines, state, cart.items).then(function (pricedLines) {
            var hasSplitPricing = false;
            for (var feeIdx = 0; feeIdx < pricedLines.length; feeIdx++) {
              var pricing = pricedLines[feeIdx] && pricedLines[feeIdx].pricing;
              if (!pricing) continue;
              if (pricing.hasFixedFee || pricing.hasPercentageFee || pricing.hasAssignedFee) {
                hasSplitPricing = true;
                break;
              }
            }
            var cartFeeLines = buildCartFeesRequestLines(
              pricedLines.map(function (line, index) {
                var cartItem = cart.items[index];
                var baseUnitCents =
                  line.pricing && line.pricing.baseUnitCents != null
                    ? line.pricing.baseUnitCents
                    : cartItem && cartItem.original_price != null
                      ? cartItem.original_price
                      : cartItem && cartItem.price != null
                        ? cartItem.price
                        : null;
                var discountedUnitCents =
                  line.pricing && line.pricing.baseUnitCents != null && cartItem
                    ? discountedUnitCentsForFeeRequest(line.pricing.baseUnitCents, cartItem)
                    : line.discountedUnitCents;
                return {
                  variantId: line.variantId,
                  quantity: line.quantity,
                  defaultUnitCents: baseUnitCents,
                  discountedUnitCents: discountedUnitCents,
                  payload: line.pricing
                    ? {
                        baseUnitCents: line.pricing.baseUnitCents,
                        unitCents:
                          line.pricing.baseUnitCents != null
                            ? line.pricing.baseUnitCents
                            : line.pricing.unitCents
                      }
                    : null
                };
              })
            );
            return fetchCartFees(cartFeeLines, state, discountCodes)
              .then(function (cartFeesData) {
                var apiPercentageFees = percentageFeesFromCartFeesResponse(cartFeesData);
                if (apiPercentageFees && apiPercentageFees.totalCents > 0) {
                  hasSplitPricing = true;
                }
                return {
                  pricedLines: pricedLines,
                  hasSplitPricing: hasSplitPricing,
                  apiPercentageFees: apiPercentageFees
                };
              })
              .catch(function () {
                return {
                  pricedLines: pricedLines,
                  hasSplitPricing: hasSplitPricing,
                  apiPercentageFees: null
                };
              });
          }).then(function (checkoutPayload) {
            var pricedLines = checkoutPayload.pricedLines;
            var hasSplitPricing = checkoutPayload.hasSplitPricing;
            var apiPercentageFees = checkoutPayload.apiPercentageFees;
            if (hasSplitPricing) {
              pricedLines = pricedLines.map(function (line, index) {
                return applySplitProductPricingToDraftLine(line, cart.items[index]);
              });
            }
            pricedLines = applyOverridePercentageFeesToDraftLines(
              pricedLines,
              cart.items,
              apiPercentageFees,
              state
            );
            var feeLines = buildDraftCheckoutFeeLines(pricedLines, cart.items);
            return fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({
                lines: pricedLines,
                feeLines: feeLines.length ? feeLines : undefined,
                pricingModel: hasSplitPricing ? 'split' : 'combined',
                discountCodes: discountCodes.length ? discountCodes : undefined,
                attributes: mergedAttributes,
                customAttributes: customAttributes,
                zapiet: {
                  checkoutMethod: zapietValidation.method,
                  attributes: mergedAttributes
                },
                state: state,
                note: findCartNote(sourceForm || document) || cart.note || 'Draft checkout from cart'
              })
            })
              .then(function (resp) {
                return resp.json().catch(function () {
                  return null;
                }).then(function (data) {
                  if (!resp.ok || !data || !data.ok || !data.checkoutUrl) {
                    throw new Error((data && (data.message || data.error)) || 'Could not create checkout. Please try again.');
                  }
                  return data.checkoutUrl;
                });
              });
          });
        });
      });
  }

  function isSafeCheckoutUrl(url) {
    if (!url || typeof url !== 'string') return false;
    try {
      var parsed = new URL(url, window.location.origin);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
      return true;
    } catch (e) {
      return false;
    }
  }

  function isCheckoutSubmitButton(el) {
    if (!el || !el.matches) return false;
    if (el.matches('[data-draft-checkout]')) return true;
    if (el.matches('button[name="checkout"], input[name="checkout"], #checkout')) return true;
    return false;
  }

  function isDraftEligibleForm(form) {
    return !!(form && form.id === 'cart_form');
  }

  var CART_UPDATE_PATH = '/cart';

  function resetCartFormActionForUpdate(form) {
    if (!form || form.id !== 'cart_form') return;
    if (form.getAttribute('action') !== CART_UPDATE_PATH) {
      form.setAttribute('action', CART_UPDATE_PATH);
    }
  }

  function isCartTemplateUpdateInteraction(target) {
    if (!target || !target.closest) return false;
    if (!isCartTemplateInteractionTarget(target)) return false;
    return !!target.closest('[data-func], [data-remove-item], [data-cart-quantity-input]');
  }

  function handleMiniCartCheckoutRedirect(button) {
    if (!button) return;
    var form = button.closest ? button.closest('form') : null;
    var tos = findTosCheckbox(form || document);
    if (tos && tos.required && !tos.checked) {
      alert('Please agree to the terms of service.');
      tos.focus();
      return;
    }
    window.location.href = '/cart';
  }

  function rememberDraftCheckoutIntent(button) {
    draftCheckoutIntent = {
      button: button || null,
      at: Date.now()
    };
  }

  function getRecentDraftCheckoutIntent() {
    if (!draftCheckoutIntent) return null;
    if (Date.now() - draftCheckoutIntent.at > DRAFT_INTENT_WINDOW_MS) {
      draftCheckoutIntent = null;
      return null;
    }
    return draftCheckoutIntent;
  }

  function clearDraftCheckoutIntent() {
    draftCheckoutIntent = null;
  }

  function isExplicitCheckoutSubmission(submitter) {
    return !!(submitter && isCheckoutSubmitButton(submitter));
  }

  function resolveCheckoutButton(form, submitter) {
    if (submitter && isCheckoutSubmitButton(submitter)) return submitter;
    var intent = getRecentDraftCheckoutIntent();
    if (intent && intent.button && intent.button.isConnected) return intent.button;
    if (!form || !form.querySelector) return null;
    return (
      form.querySelector('[data-draft-checkout]') ||
      form.querySelector('button[name="checkout"], input[name="checkout"], #checkout')
    );
  }

  function installDraftCheckoutSubmitPatch() {
    if (installSubmitPatchDone) return;
    installSubmitPatchDone = true;
    if (typeof HTMLFormElement === 'undefined' || !HTMLFormElement.prototype) return;

    if (typeof window !== 'undefined' && typeof window.aboffsNativeSubmit !== 'function') {
      window.aboffsNativeSubmit = HTMLFormElement.prototype.submit;
    }
    var nativeSubmit =
      (typeof window !== 'undefined' && typeof window.aboffsNativeSubmit === 'function'
        ? window.aboffsNativeSubmit
        : HTMLFormElement.prototype.submit);
    HTMLFormElement.prototype.submit = function () {
      var form = this;
      if (!form || !form.querySelector || form.id !== 'cart_form') {
        return nativeSubmit.apply(form, arguments);
      }

      var intent = getRecentDraftCheckoutIntent();
      if (!intent) {
        resetCartFormActionForUpdate(form);
        return nativeSubmit.apply(form, arguments);
      }

      var button = resolveCheckoutButton(form, null);
      if (!button) {
        return nativeSubmit.apply(form, arguments);
      }

      startDraftCheckoutFlow(button, form);
    };
  }

  function startDraftCheckoutFlow(button, providedForm) {
    if (!button) return;
    if (button.getAttribute('data-draft-checkout-inflight') === '1') return;
    clearDraftCheckoutIntent();
    button.setAttribute('data-draft-checkout-inflight', '1');

    var root = providedForm || button.closest('form') || button.closest('[data-cart-form]') || document;
    var tos = findTosCheckbox(root);
    if (tos && tos.required && !tos.checked) {
      alert('Please agree to the terms of service.');
      tos.focus();
      button.removeAttribute('data-draft-checkout-inflight');
      return;
    }

    var originalHtml = button.innerHTML;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Creating checkout...';

    createDraftCheckout(root)
      .then(function (checkoutUrl) {
        if (!isSafeCheckoutUrl(checkoutUrl)) {
          throw new Error('Invalid checkout URL from API');
        }
        window.location.href = checkoutUrl;
      })
      .catch(function (err) {
        console.error('[aboffs-pricing] Draft checkout failed:', err);
        if (err && err.code === 'ZAPIET_CHECKOUT_REQUIRED') {
          showZapietCheckoutNotice(err.message);
          focusZapietWidget();
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.innerHTML = originalHtml;
          button.removeAttribute('data-draft-checkout-inflight');
          return;
        }
        if (
          err &&
          (err.code === 'STATE_PRICING_UNAVAILABLE' || err.code === 'STATE_PRICING_FETCH_FAILED')
        ) {
          alert(err.message || 'One or more items are unavailable in your location.');
          button.disabled = false;
          button.removeAttribute('aria-busy');
          button.innerHTML = originalHtml;
          button.removeAttribute('data-draft-checkout-inflight');
          return;
        }
        console.warn('[aboffs-pricing] Falling back to standard checkout:', err);
        var formEl = root && root.tagName === 'FORM' ? root : button && button.closest ? button.closest('form') : null;
        if (formEl && typeof window !== 'undefined' && typeof window.aboffsNativeSubmit === 'function') {
          button.removeAttribute('data-draft-checkout-inflight');
          window.aboffsNativeSubmit.apply(formEl);
          return;
        }
        alert(err && err.message ? err.message : 'Checkout failed');
        button.disabled = false;
        button.removeAttribute('aria-busy');
        button.innerHTML = originalHtml;
        button.removeAttribute('data-draft-checkout-inflight');
      });
  }

  function installCartDiscountHandler() {
    function refreshCartAfterDiscountChange(cart) {
      cacheCartLineQuantities(cart);
      renderCartDiscounts(cart);
      setCartDiscountMessage('', '');
      if (typeof document !== 'undefined' && document.dispatchEvent) {
        document.dispatchEvent(new CustomEvent('aboffs:cartRefreshed', { detail: { cart: cart } }));
      }
      scheduleApplyImmediate({ scope: 'cart' });
    }

    function handleDiscountApply(trigger) {
      var root = trigger && trigger.closest ? trigger.closest('[data-aboffs-cart-discount]') : null;
      var input = root ? root.querySelector('[data-aboffs-discount-input]') : null;
      var button = root ? root.querySelector('[data-aboffs-discount-apply]') : null;
      var code = input ? normalizeDiscountCode(input.value) : '';
      if (!code) {
        setCartDiscountMessage(cartDiscountMessage('invalid'), 'error');
        if (input && input.focus) input.focus();
        return;
      }
      if (button) {
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      }
      setCartDiscountMessage('', '');
      fetchCartJson()
        .catch(function () {
          return null;
        })
        .then(function (cart) {
          var codes = mergeDiscountCodeList(discountCodesFromCart(cart), code);
          clearCartFeesCache();
          return applyCartDiscountCodes(codes).then(function () {
            return fetchCartJson();
          });
        })
        .then(function (cart) {
          if (!cart) throw new Error('cart unavailable');
          if (!isDiscountCodeApplied(cart, code)) {
            throw new Error('not applied');
          }
          if (input) input.value = '';
          setCartDiscountMessage(cartDiscountMessage('applied'), 'success');
          refreshCartAfterDiscountChange(cart);
        })
        .catch(function () {
          setCartDiscountMessage(cartDiscountMessage('not_applied'), 'error');
        })
        .finally(function () {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
        });
    }

    function handleDiscountRemove(button) {
      var code = button ? button.getAttribute('data-aboffs-discount-remove') : '';
      if (!code) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      fetchCartJson()
        .catch(function () {
          return null;
        })
        .then(function (cart) {
          var codes = removeDiscountCodeFromList(discountCodesFromCart(cart), code);
          clearCartFeesCache();
          return applyCartDiscountCodes(codes).then(function () {
            return fetchCartJson();
          });
        })
        .then(function (cart) {
          if (!cart) throw new Error('cart unavailable');
          refreshCartAfterDiscountChange(cart);
        })
        .catch(function () {
          setCartDiscountMessage(cartDiscountMessage('not_applied'), 'error');
        })
        .finally(function () {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }
        });
    }

    document.addEventListener('click', function (e) {
      var removeButton =
        e.target && e.target.closest ? e.target.closest('[data-aboffs-discount-remove]') : null;
      if (removeButton) {
        e.preventDefault();
        handleDiscountRemove(removeButton);
        return;
      }

      var button = e.target && e.target.closest ? e.target.closest('[data-aboffs-discount-apply]') : null;
      if (!button) return;
      e.preventDefault();
      handleDiscountApply(button);
    });

    document.addEventListener('keydown', function (e) {
      if (!e || e.key !== 'Enter') return;
      var input = e.target && e.target.matches ? e.target : null;
      if (!input || !input.matches('[data-aboffs-discount-input]')) return;
      e.preventDefault();
      handleDiscountApply(input);
    });
  }

  function installCartFormUpdateGuard() {
    // Zapiet sets #cart_form action to /checkout once pickup is complete. Theme cart
    // qty/remove refreshes submit that form and must stay on /cart.
    document.addEventListener(
      'click',
      function (e) {
        var target = e.target && e.target.closest ? e.target.closest('[data-func], [data-remove-item], [data-cart-quantity-input]') : null;
        if (!isCartTemplateUpdateInteraction(target)) return;
        clearDraftCheckoutIntent();
        resetCartFormActionForUpdate(document.getElementById('cart_form'));
      },
      true
    );

    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!isDraftEligibleForm(form)) return;
        if (isExplicitCheckoutSubmission(e.submitter || null)) return;
        clearDraftCheckoutIntent();
        resetCartFormActionForUpdate(form);
      },
      true
    );
  }

  function installDraftCheckoutHandler() {
    installCartFormUpdateGuard();
    installDraftCheckoutSubmitPatch();

    document.addEventListener(
      'click',
      function (e) {
        var redirectTarget =
          e.target && e.target.closest ? e.target.closest('[data-cart-checkout-redirect]') : null;
        if (redirectTarget) {
          e.preventDefault();
          handleMiniCartCheckoutRedirect(redirectTarget);
          return;
        }

        var target = e.target && e.target.closest ? e.target.closest('button, input[type="submit"], #checkout, [data-draft-checkout]') : null;
        if (!target || !isCheckoutSubmitButton(target)) return;
        var form = target.form || (target.closest ? target.closest('form') : null);
        if (!isDraftEligibleForm(form)) return;
        rememberDraftCheckoutIntent(target);
      },
      true
    );

    // Run on bubbling submit so Zapiet handlers can validate/mutate first.
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.querySelector) return;
      if (e.defaultPrevented) return;
      if (!isDraftEligibleForm(form)) return;

      var submitter = e.submitter || null;
      // Cart qty/remove uses jQuery .submit(), which fires this event without a
      // submitter — must not be treated as checkout.
      if (!isExplicitCheckoutSubmission(submitter)) return;

      if (
        typeof window.__aboffsPickupNoSlotsBlocksCheckout === 'function' &&
        window.__aboffsPickupNoSlotsBlocksCheckout()
      ) {
        e.preventDefault();
        showZapietCheckoutNotice(
          'No pickup times are available for this date. Please choose another date.'
        );
        focusZapietWidget();
        return;
      }

      if (submitter && isCheckoutSubmitButton(submitter)) {
        rememberDraftCheckoutIntent(submitter);
      }

      var button = resolveCheckoutButton(form, submitter);
      if (!button) return;

      clearZapietCheckoutNotice();
      e.preventDefault();
      startDraftCheckoutFlow(button, form);
    });
  }

  function installObservers() {
    // Capture-phase listeners run before theme variant callbacks mutate `.current_price`.
    // This prevents a one-frame flash of default Shopify price during variant switches.
    document.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target) return;
        if (isVariantChangeTarget(target)) {
          primeProductPriceMask(target);
        }
      },
      true
    );

    document.addEventListener(
      'click',
      function (e) {
        var target = e.target && e.target.closest ? e.target.closest('[data-func], .swatch-element') : null;
        if (target) {
          primeProductPriceMask(target);
        }
      },
      true
    );

    document.addEventListener('change', function (e) {
      var target = e.target;
      if (!target) return;
      if (isVariantChangeTarget(target)) {
        primeProductPriceMask(target);
      }
      if (target.name === 'updates[]') {
        if (isCartTemplateInteractionTarget(target)) {
          clearDraftCheckoutIntent();
          return;
        }
        scheduleApply({ scope: 'cart' });
        return;
      }
      if (target.matches && target.matches('.single-option-selector')) {
        scheduleProductVariantPricing(target);
        return;
      }
      if (
        target.name === 'id' ||
        (target.matches && target.matches('select[name="id"], input[name="id"]'))
      ) {
        scheduleProductVariantPricing(target);
      }
    });

    document.addEventListener('click', function (e) {
      var target = e.target && e.target.closest ? e.target.closest('[data-func], .swatch-element, [data-remove-item]') : null;
      if (!target) return;
      if (target.closest('.product_section, .js-product_section, .product-quickshop, .js-quick-shop')) {
        primeProductPriceMask(target);
        return;
      }
      if (isCartTemplateInteractionTarget(target)) {
        clearDraftCheckoutIntent();
        return;
      }
      if (target.matches && target.matches('[data-remove-item]')) {
        scheduleApply({ scope: 'cart' });
      }
    });

    document.addEventListener(PRODUCT_RECOMMENDATIONS_RENDERED_EVENT, scheduleApplyImmediate);
    document.addEventListener('click', function (e) {
      var tabLink = e.target && e.target.closest ? e.target.closest('.product-tabs ul.tabs > li > a[href^="#"]') : null;
      if (!tabLink) return;
      var panelId = String(tabLink.getAttribute('href') || '').trim();
      if (!panelId || panelId.charAt(0) !== '#') return;
      var panel = document.querySelector(panelId);
      if (!panel || !panel.classList || !panel.classList.contains('tab-content-item')) return;
      syncHomepageTabPanelActive(panel);
      fetchListingPanelIfNeeded(panel, { showLoader: true });
    });
    document.addEventListener(SEARCH_SUGGESTIONS_RENDERED_EVENT, scheduleApplyImmediate);
    document.addEventListener('shopify:section:load', scheduleApply);
    document.addEventListener('shopify:section:reorder', scheduleApply);
    // Cart drawer HTML is rebuilt from /cart response data.
    // Cache quantities from the payload so subtotal math uses authoritative
    // line quantities (not transient input values during +/- interactions).
    document.addEventListener('aboffs:cartRefreshed', function (e) {
      cacheCartLineQuantities(e && e.detail ? e.detail.cart : null);
      scheduleApplyImmediate({ scope: 'full' });
    });

    if (typeof MutationObserver !== 'undefined') {
      var relevantSelector =
        '.js-price-override, .modal_price .current_price, [data-cart-item][data-variant-id], .js-cart-subtotal-money, .js-cart-unit-price, .js-cart-line-total';

      function mutationAddsNewPricingSurface(node) {
        if (!node || node.nodeType !== 1) return false;
        if (node.matches && node.matches(relevantSelector)) return true;
        if (node.closest && node.closest('.modal_price .current_price[data-price-hydrated], .js-price-override[data-price-hydrated]')) {
          return false;
        }
        return !!(node.querySelector && node.querySelector(relevantSelector));
      }

      new MutationObserver(function () {
        var shouldApply = false;
        for (var i = 0; i < arguments.length && !shouldApply; i++) {
          var mutation = arguments[i];
          var added = mutation.addedNodes || [];
          for (var a = 0; a < added.length && !shouldApply; a++) {
            if (mutationAddsNewPricingSurface(added[a])) shouldApply = true;
          }
        }
        if (shouldApply) scheduleApply({ scope: 'full' });
      }).observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      clearLegacyStateOverride();
      installObservers();
      installCartDiscountHandler();
      installDraftCheckoutHandler();
      preHideDirectPriceNodes();
      releaseInitialPriceMask();
      scheduleApply();
    });
  } else {
    clearLegacyStateOverride();
    installObservers();
    installCartDiscountHandler();
    installDraftCheckoutHandler();
    preHideDirectPriceNodes();
    releaseInitialPriceMask();
    scheduleApply();
  }
})();
